<script setup lang="ts">
import BoxWithdrawal from './BoxWithdrawal.vue';
import BoxSetupTutorial from './BoxSetupTutorial.vue';
import OfferChangeStats from './OfferChangeStats.vue';
import { isOfferChangeSummary, type OfferChangeSummary } from '../offer-change-stats';
import OrderReference from './OrderReference.vue';
import TravelerCommentReview from './TravelerCommentReview.vue';
import type { CommentReview } from '../order-management';
import Beds24Reservations from './Beds24Reservations.vue';
import ReservationSummary from './ReservationSummary.vue';
import type { ReservationSummary as Reservation } from '../reservation';
import AnnualPerformance from './AnnualPerformance.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useOperatorTabHistory } from '../operator-tab-history';
import OrderManagement from './OrderManagement.vue';
import OfferGallery from './OfferGallery.vue';
import PaymentNotifications from './PaymentNotifications.vue';
import TaskAccordion from './TaskAccordion.vue';
import {
  modificationLabel,
  modificationBlocks,
  cancellationLabel,
  type Cancellation,
  type Modification,
  type OrderFunding,
} from '../order-management';
import OperationalNavigation from './OperationalNavigation.vue';
import { createPublicQrDataUrl } from '../qr';

const props = withDefaults(
  defineProps<{
    managementMode?: boolean;
    section?: 'dashboard' | 'catalog' | 'boxes';
  }>(),
  {
    managementMode: false,
    section: 'dashboard',
  },
);

const managementSection = useOperatorTabHistory(
  ['', 'team', 'apartments', 'tutorials', 'reservations'],
  '',
);
const managementSections = [
  { id: 'team', title: 'Équipe', description: 'Comptes et logements confiés', icon: '👥' },
  {
    id: 'apartments',
    title: 'Logements',
    description: 'Adresses, offres et capacité',
    icon: '⌂',
  },
  { id: 'tutorials', title: 'Tutoriels', description: 'Guides en images et vidéos', icon: '▷' },
  { id: 'reservations', title: 'Réservations', description: 'Liaison avec Beds24', icon: '▦' },
];
type AdminBox = {
  withdrawalRequestId?: string | null;
  boxId: string;
  displayName: string;
  offerId?: string;
  amountMinor: number;
  currency: 'EUR';
  priceVersion: string;
  publicQrToken: string;
  qrUrl: string;
  availabilityState: 'AVAILABLE' | 'ASSIGNED_OR_IN_USE';
  conditionState?: 'ACTIVE' | 'THEFT' | 'BREAKAGE';
  travelerConsumed?: boolean;
  currentDeliveryRequestId: string | null;
  currentDeliveryAssignment?: {
    deliveryRequestId: string;
    deliveryLifecycleState?: DeliveryLifecycle;
    fulfillmentState?: 'PENDING' | 'FULFILLED';
    requestedOfferId: string;
    requestedOfferDisplayName?: string;
    travelerName?: string;
    apartmentName?: string;
  };
};

type BoxOffer = {
  offerId: string;
  displayName: string;
  amountMinor: number;
  currency: 'EUR';
  priceVersion: string;
  products?: OfferProduct[];
  totalPurchasePriceMinor?: number;
  recommendedSalePriceMinor?: number;
  recommendedMarginPolicy?: 'ESTIMATED_DEFAULT_PURCHASE_PRICE_X_2_0';
  recommendedSalePriceSource?: 'ESTIMATED_DEFAULT' | 'ADMIN_OVERRIDE';
  minimumSaleCoefficient?: number;
  minimumSalePriceMinor?: number;
  photoDataUrl?: string;
};

type OfferProduct = {
  name: string;
  quantity: number;
  purchasePriceMinor: number;
};

type OfferProductDraft = {
  name: string;
  quantity: string;
  purchasePriceEuros: string;
};

type DeliveryLifecycle =
  | 'RECEIVED'
  | 'MODIFICATION_PROPOSEE'
  | 'REFUSEE'
  | 'PREPARING'
  | 'READY'
  | 'DELIVERED'
  | 'CANCELLED';
type ProviderException = {
  providerId: string;
  proposedAlternative: string | null;
  refusalReason: string | null;
  reportedAt: string;
};
type PurchaseAlert = {
  missingProductName: string;
  missingQuantity: number;
  replacementProductName: string | null;
  actualQuantity: number;
  providerId: string;
  status: 'OPEN';
};
type ProviderOfferChangeAlert = {
  providerId: string;
  previousOfferId: string;
  previousOfferDisplayName: string;
  newOfferId: string;
  newOfferDisplayName: string;
  changedAt: string;
  status: 'OPEN';
};
type AdminBoxLockCode = {
  boxId: string;
  lockCode: string | null;
  codeLastChangedAt?: string | null;
  ageDays?: number | null;
  renewalDue?: boolean;
  renewalDays?: number | null;
};
type CurrentDeliveryRequest = {
  canRefuseUnpaidOrder?: boolean;
  adminRefusal?: import('../order-management').AdminRefusal;
  prepaymentStatus?: 'PAID' | 'NOT_PREPAID' | 'AUTHORIZED' | 'EXPIRED' | 'CANCELED';
  funding?: OrderFunding;
  payment?: { paymentAttemptId: string; amountMinor: number; currency: 'EUR'; paidAt: string };
  deliveryRequestKind: 'CURRENT_LOGISTICS';
  deliveryRequestId: string;
  deliveryLifecycleState: DeliveryLifecycle;
  cancellation?: Cancellation;
  modifications?: Modification[];
  selectedBoxOfferId: string;
  selectedBoxOfferDisplayNameSnapshot: string;
  selectedBoxOfferAmountMinorSnapshot: number;
  selectedBoxOfferCurrencySnapshot: 'EUR';
  selectedBoxOfferPriceVersionSnapshot: string;
  preparedBoxOfferId?: string | null;
  preparedByProviderId?: string | null;
  purchaseAlerts?: PurchaseAlert[];
  providerOfferChangeAlert?: ProviderOfferChangeAlert;
  temporaryPreparation?: {
    displayName: string;
    canonicalOfferId: string;
    products: Array<{
      expectedName: string;
      expectedQuantity: number;
      actualName: string;
      actualQuantity: number;
    }>;
  };
  configuredAssignment?: boolean;
  travelerOrder?: boolean;
  configuredAssignmentSlotNumber?: number;
  createdAt: string;
  travelerName: string;
  travelerComment?: string;
  travelerCommentReview?: CommentReview | null;
  reservation?: Reservation;
  apartmentName: string;
  deliveryDeadlineAt: string;
  absentEntryAuthorized: true;
  providerException: ProviderException | null;
  sourceBoxId: string | null;
  sourceBoxDisplayName: string;
  assignedBox?: AdminBox;
  availablePhysicalBoxes?: AdminBox[];
};

type DeliveryRequest = CurrentDeliveryRequest;

type UpsellMetrics = {
  upsellRevenueMinor: number;
  upsellSalesCount: number;
  averageUpsellBasketMinor: number;
  estimatedGrossMarginMinor: number | null;
  grossMarginEvidence: 'ESTIMATED_CURRENT_CATALOG_COST' | 'INCONNU_INSUFFICIENT_COST_EVIDENCE';
};

type PaidSale = {
  paymentAttemptId: string;
  deliveryRequestId: string | null;
  paidAt: string;
  amountMinor: number;
  apartmentName: string | null;
  saleMode: string;
};
type UpsellDashboard = {
  offerChangesByScope?: Record<string, OfferChangeSummary>;
  salesByScope?: Record<string, PaidSale[]>;
  period?: 'CURRENT_MONTH' | 'CURRENT_YEAR' | 'ALL';
  filters: Array<{
    key: string;
    kind: 'ALL' | 'APARTMENT' | 'OWNER';
    value?: string;
    label: string;
  }>;
  metricsByScope: Record<string, UpsellMetrics>;
  pieSlicesByScope?: Record<string, Array<{ label: string; valueMinor: number }>>;
  selectedOfferId?: string;
  selectedSaleMode?: 'ALL' | 'ORDERED' | 'SELF_SERVICE';
  offerFilters?: Array<{ key: string; label: string }>;
  saleModeFilters?: Array<{
    key: 'ALL' | 'ORDERED' | 'SELF_SERVICE';
    label: string;
  }>;
};

type ProviderScopes = { cities: string[]; apartments: string[]; owners: string[] };
type ProviderAccount = {
  providerId: string;
  username: string;
  active: boolean;
  scopes: ProviderScopes;
  createdAt: string;
  updatedAt: string;
};
type ProviderAccountDraft = {
  providerId: string;
  username: string;
  password: string;
  scopes: ProviderScopes;
};
type ApartmentConfiguration = {
  apartment: string;
  city?: string | null;
  owner: string;
  streetNumber?: string | null;
  listingPhotoDataUrl?: string;
  offerIds: string[];
  activeBoxTarget: number;
  activeBoxCount: number;
  updatedAt: string;
  offers: Array<{ offerId: string; displayName: string }>;
};
type OperationalEstateEntry = {
  key: string;
  apartment: string;
  city: string | null;
  client: string | null;
  providerIds: string[];
  deliveryRequestId: string | null;
  physicalBoxId: string | null;
  boxDisplayName: string | null;
  offerId: string | null;
  offer: string | null;
  tone: 'to-prepare' | 'consumed' | 'delivered' | 'installed';
  label: 'Préparer' | 'Vendue' | 'Commande' | 'Prête';
  slotNumber: number | null;
  requiresPhysicalBox: boolean;
  eligiblePhysicalBoxCount: number;
  purchaseAlerts: PurchaseAlert[];
  offerChangeAlert: ProviderOfferChangeAlert | null;
};
type OperationalBanner = {
  consumableAlerts: PurchaseAlert[];
  missingBoxCount: number;
  noEligibleAlerts: Array<{
    apartmentName: string;
    providerId: string;
    reason: 'NO_ELIGIBLE_PHYSICAL_BOX';
  }>;
  pendingRecoveryCount: number;
  damageCount: number;
  theftCount: number;
};

const boxes = ref<AdminBox[]>([]);
const boxOffers = ref<BoxOffer[]>([]);
const selectedOfferId = ref('');
const deliveryRequests = ref<DeliveryRequest[]>([]);
const selected = ref<AdminBox | null>(null);
const editingNameBoxId = ref('');
const editingOfferPriceId = ref('');
const editingOfferNameId = ref('');
const editingOfferProductsId = ref('');
const editingLockCodeBoxId = ref('');
const lockCodeDraft = ref('');
const boxLockCodes = ref<Record<string, string | null>>({});
const boxLockCodeEntries = ref<Record<string, AdminBoxLockCode>>({});
const expandedAlertKey = ref('');
const displayName = ref('');
const offerAmountEuros = ref('');
const offerDisplayName = ref('');
const offerProductsDraft = ref<OfferProductDraft[]>([]);
const editingOfferCoefficientId = ref('');
const offerCoefficient = ref('2.0');
const newBoxNumber = ref('');
const newBoxOwner = ref('');
const newBoxApartments = ref<string[]>([]);
const newLockCode = ref('000');
const newOfferDisplayName = ref('');
const newOfferAmountEuros = ref('');
const boxesLoading = ref(true);
const offersLoading = ref(true);
const deliveryLoading = ref(true);
const saving = ref(false);
const boxesError = ref('');
const offersError = ref('');
const deliveryError = ref('');
const actionError = ref('');
const actionSuccess = ref('');
const upsellDashboard = ref<UpsellDashboard | null>(null);
const dashboardScope = ref('ALL');
const dashboardPeriod = ref<'CURRENT_MONTH' | 'CURRENT_YEAR' | 'ALL'>('CURRENT_MONTH');
const dashboardOfferId = ref('ALL');
const dashboardSaleMode = ref<'ALL' | 'ORDERED' | 'SELF_SERVICE'>('ALL');
const dashboardLoading = ref(true);
const dashboardError = ref('');
let dashboardLoadVersion = 0;
const operationalStatusFilter = ref('ALL');
const operationalProviderFilter = ref('ALL');
const operationalClientFilter = ref('ALL');
const operationalCityFilter = ref('ALL');
const operationalApartmentFilter = ref('ALL');
const catalogOwnerFilter = ref('ALL');
const catalogProviderFilter = ref('ALL');
const catalogCityFilter = ref('ALL');
const catalogOperationalCapacityFilter = ref('ALL');
const accountsOwnerFilter = ref('ALL');
const accountsProviderFilter = ref('ALL');
const accountsCityFilter = ref('ALL');
const accountsOperationalCapacityFilter = ref('ALL');
const boxesOwnerFilter = ref('ALL');
const boxesProviderFilter = ref('ALL');
const boxesCityFilter = ref('ALL');
const boxesOperationalCapacityFilter = ref('ALL');
const operationalEstate = ref<OperationalEstateEntry[]>([]);
const operationalBanner = ref<OperationalBanner | null>(null);
const operationalEstateLoading = ref(true);
const operationalEstateError = ref('');
const providerAccounts = ref<ProviderAccount[]>([]);
const providerScopeOptions = ref<ProviderScopes>({ cities: [], apartments: [], owners: [] });
const providerScopeDrafts = ref<ProviderScopes>({ cities: [], apartments: [], owners: [] });
const providerAccountsLoading = ref(true);
const editingProviderId = ref('');
const providerFormOpen = ref(false);
const providerScopeError = ref('');
const providerDraft = ref<ProviderAccountDraft>({
  providerId: '',
  username: '',
  password: '',
  scopes: { cities: [], apartments: [], owners: [] },
});
const providerScopeDimensions = [
  { key: 'cities', label: 'Villes' },
  { key: 'apartments', label: 'Appartements' },
] as const;
const apartmentConfigurations = ref<ApartmentConfiguration[]>([]);
const apartmentConfigurationFormOpen = ref(false);
const apartmentConfigurationDraft = ref({
  apartment: '',
  city: '',
  owner: '',
  streetNumber: '',
  offerIds: [] as string[],
  activeBoxTarget: 1,
});
const boxOwnerOptions = computed(() =>
  [...new Set(apartmentConfigurations.value.map((configuration) => configuration.owner))].sort(
    (left, right) => left.localeCompare(right, 'fr'),
  ),
);
const boxApartmentOptions = computed(() =>
  apartmentConfigurations.value
    .filter((configuration) => configuration.owner === newBoxOwner.value)
    .sort((left, right) => left.apartment.localeCompare(right.apartment, 'fr')),
);
const selectedBoxStreetNumbers = computed(() => [
  ...new Set(
    boxApartmentOptions.value
      .filter((configuration) => newBoxApartments.value.includes(configuration.apartment))
      .map((configuration) => configuration.streetNumber)
      .filter((streetNumber): streetNumber is string => Boolean(streetNumber)),
  ),
]);
const inferredBoxStreetNumber = computed(() =>
  selectedBoxStreetNumbers.value.length === 1 ? selectedBoxStreetNumbers.value[0] : '',
);
const mixedBoxStreetNumbers = computed(() => selectedBoxStreetNumbers.value.length > 1);

function resetNewBoxApartments(): void {
  newBoxApartments.value = [];
}

function clearDefaultNewLockCode(): void {
  if (newLockCode.value === '000') newLockCode.value = '';
}

const activeTab = useOperatorTabHistory(['home', 'orders', 'stats', 'alerts']);
const selectedOrderId = ref('');
const showPaidSales = ref(false);
watch(
  activeTab,
  () => {
    selectedOrderId.value = '';
    showPaidSales.value = false;
  },
  { flush: 'sync' },
);
const orderFilter = ref('ALL');
const deliveryDataReady = computed(() => !deliveryLoading.value && !deliveryError.value);
const selectedOrder = computed(() =>
  deliveryDataReady.value
    ? deliveryRequests.value.find(
        (request) => request.deliveryRequestId === selectedOrderId.value,
      )
    : undefined,
);
const selectedSales = computed(
  () => upsellDashboard.value?.salesByScope?.[dashboardScope.value] ?? [],
);
const remainingOrders = computed(() =>
  (deliveryDataReady.value ? deliveryRequests.value : []).filter(
    (request) =>
      request.travelerOrder !== false &&
      !['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState),
  ),
);
const visibleOrders = computed(() =>
  (deliveryDataReady.value ? deliveryRequests.value : []).filter(
    (request) =>
      orderFilter.value === 'ALL' ||
      (orderFilter.value === 'REMAINING'
        ? request.travelerOrder !== false &&
          !['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)
        : ['REFUSEE', 'MODIFICATION_PROPOSEE'].includes(request.deliveryLifecycleState)),
  ),
);
function openOrders(filter = 'ALL'): void {
  activeTab.value = 'orders';
  orderFilter.value = filter;
  selectedOrderId.value = '';
}
function statusLabel(request: DeliveryRequest): string {
  if (request.adminRefusal)
    return request.adminRefusal.status === 'REFUSED'
      ? 'Refusée · empreinte libérée'
      : 'Refus demandé · libération bancaire à vérifier';
  if ('deliveryCapturePending' in request && request.deliveryCapturePending)
    return 'Encaissement de livraison à vérifier';
  if ('financeReviewRequired' in request && request.financeReviewRequired)
    return 'Rapprochement financier requis';
  if (modificationBlocks(request)) return modificationLabel(request.modifications!.at(-1)!);
  if (request.cancellation) return cancellationLabel(request.cancellation);
  return {
    RECEIVED: 'À affecter',
    PREPARING: 'À préparer',
    READY: 'À livrer',
    DELIVERED: 'Terminé',
    CANCELLED: 'Annulée',
    REFUSEE: 'Refus à traiter',
    MODIFICATION_PROPOSEE: 'Alternative à traiter',
  }[request.deliveryLifecycleState];
}
function statusTone(request: DeliveryRequest): string {
  if (request.adminRefusal)
    return request.adminRefusal.status === 'REFUSED' ? 'ops-done' : 'ops-urgent';
  if ('deliveryCapturePending' in request && request.deliveryCapturePending)
    return 'ops-urgent';
  if (request.deliveryLifecycleState === 'CANCELLED') return 'ops-done';
  if (modificationBlocks(request)) return 'ops-urgent';
  if (
    request.cancellation &&
    ['REQUESTED', 'CONTROL_FAILED'].includes(request.cancellation.status)
  )
    return 'ops-urgent';
  return ['REFUSEE', 'MODIFICATION_PROPOSEE'].includes(request.deliveryLifecycleState)
    ? 'ops-urgent'
    : request.deliveryLifecycleState === 'DELIVERED'
      ? 'ops-done'
      : 'ops-progress';
}
// Each order appears once in Pilotage, under its next outstanding action.
const taskGroups = computed(() => {
  const groups = [
    { title: 'Annulations à traiter', requests: [] as DeliveryRequest[] },
    { title: 'Modifications à traiter', requests: [] as DeliveryRequest[] },
    { title: 'Commandes bloquées', requests: [] as DeliveryRequest[] },
    { title: 'À affecter', requests: [] as DeliveryRequest[] },
    { title: 'À préparer', requests: [] as DeliveryRequest[] },
    { title: 'À livrer', requests: [] as DeliveryRequest[] },
  ];
  if (!deliveryDataReady.value) return groups;
  for (const request of deliveryRequests.value) {
    const cancellationPending =
      request.cancellation &&
      !['REJECTED', 'REFUNDED_SIMULATED', 'REFUNDED', 'SETTLED'].includes(
        request.cancellation.status,
      );
    const modificationPending = modificationBlocks(request);
    const financePending =
      (request.adminRefusal && request.adminRefusal.status !== 'REFUSED') ||
      ('deliveryCapturePending' in request && request.deliveryCapturePending) ||
      ('financeReviewRequired' in request && request.financeReviewRequired) ||
      request.funding?.bankReviewRequired ||
      request.funding?.bankOperationPending;
    if (cancellationPending) groups[0].requests.push(request);
    else if (modificationPending) groups[1].requests.push(request);
    else if (financePending) groups[2].requests.push(request);
    else if (['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState)) continue;
    else if (
      ['REFUSEE', 'MODIFICATION_PROPOSEE'].includes(request.deliveryLifecycleState) ||
      (request.travelerComment?.trim() && request.travelerCommentReview?.status !== 'APPROVED')
    )
      groups[2].requests.push(request);
    else
      groups[
        request.deliveryLifecycleState === 'RECEIVED'
          ? 3
          : request.deliveryLifecycleState === 'PREPARING'
            ? 4
            : 5
      ].requests.push(request);
  }
  return groups;
});
const operationalTasks = computed(() => {
  const banner = operationalBanner.value;
  if (!banner) return [];
  const tasks: string[] = [];
  for (const name of new Set(banner.consumableAlerts.map((alert) => alert.missingProductName)))
    tasks.push(`Réapprovisionner : ${name}`);
  if (banner.missingBoxCount) tasks.push(`Boxes manquantes : ${banner.missingBoxCount}`);
  for (const alert of banner.noEligibleAlerts)
    tasks.push(`Box à trouver : ${alert.apartmentName}`);
  if (banner.pendingRecoveryCount)
    tasks.push(`Boxes à récupérer : ${banner.pendingRecoveryCount}`);
  if (banner.damageCount) tasks.push(`Boxes endommagées : ${banner.damageCount}`);
  if (banner.theftCount) tasks.push(`Boxes volées : ${banner.theftCount}`);
  for (const entry of operationalEstate.value.filter(
    (entry) => entry.offerChangeAlert?.status === 'OPEN',
  ))
    tasks.push(`Offre modifiée à vérifier : ${entry.apartment}`);
  return tasks;
});
const navigation = computed(() => [
  { id: 'home', label: 'Pilotage', ...(!showDashboard.value ? { href: '/admin' } : {}) },
  {
    id: 'orders',
    label: 'Commandes',
    ...(!showDashboard.value ? { href: '/admin#orders' } : {}),
  },
  { id: 'stats', label: 'Stats', ...(!showDashboard.value ? { href: '/admin#stats' } : {}) },
  { id: 'catalog', label: 'Catalogue', href: '/admin/catalog' },
  { id: 'more', label: 'Gestion', href: '/admin/providers-apartments' },
]);
function selectTab(tab: string): void {
  activeTab.value = tab;
  selectedOrderId.value = '';
  showPaidSales.value = false;
}
const showDashboard = computed(() => !props.managementMode && props.section === 'dashboard');
const showCatalog = computed(() => !props.managementMode && props.section === 'catalog');
const showPhysicalBoxes = computed(() => !props.managementMode && props.section === 'boxes');

type AdminPageFilterMetadata = {
  owners: string[];
  providers: string[];
  cities: string[];
  operationalCapacities: string[];
};

const ADMIN_BOX_FILTER_LABEL_PATTERN = /^Box_[1-9]\d*_([\p{Lu}][\p{Lu}'-]*)_([1-9]\d*)$/u;

function sortedUnique(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].sort(
    (left, right) => left.localeCompare(right, 'fr'),
  );
}

function operationalCapacity(configuration: ApartmentConfiguration): string {
  return `${configuration.activeBoxCount}/${configuration.activeBoxTarget}`;
}

function sortedOperationalCapacities(values: string[]): string[] {
  return [...new Set(values)].sort((left, right) => {
    const [leftCount, leftTarget] = left.split('/').map(Number);
    const [rightCount, rightTarget] = right.split('/').map(Number);
    return leftCount - rightCount || leftTarget - rightTarget;
  });
}

function providerMatchesApartment(
  account: ProviderAccount,
  configuration: ApartmentConfiguration,
): boolean {
  return (
    (account.scopes.cities.length === 0 ||
      (typeof configuration.city === 'string' &&
        account.scopes.cities.includes(configuration.city))) &&
    (account.scopes.apartments.length === 0 ||
      account.scopes.apartments.includes(configuration.apartment)) &&
    (account.scopes.owners.length === 0 || account.scopes.owners.includes(configuration.owner))
  );
}

function providersForApartments(configurations: ApartmentConfiguration[]): string[] {
  return sortedUnique(
    configurations.flatMap((configuration) =>
      providerAccounts.value
        .filter((account) => account.active && providerMatchesApartment(account, configuration))
        .map((account) => account.providerId),
    ),
  );
}

function metadataForOffer(offer: BoxOffer): AdminPageFilterMetadata {
  const configurations = apartmentConfigurations.value.filter((configuration) =>
    configuration.offerIds.includes(offer.offerId),
  );
  return {
    owners: sortedUnique(configurations.map((configuration) => configuration.owner)),
    providers: providersForApartments(configurations),
    cities: sortedUnique(configurations.map((configuration) => configuration.city)),
    operationalCapacities: sortedOperationalCapacities(configurations.map(operationalCapacity)),
  };
}

function metadataForAccount(account: ProviderAccount): AdminPageFilterMetadata {
  const configurations = apartmentConfigurations.value.filter((configuration) =>
    providerMatchesApartment(account, configuration),
  );
  return {
    owners: sortedUnique(
      account.scopes.owners.length > 0
        ? account.scopes.owners
        : configurations.map((configuration) => configuration.owner),
    ),
    providers: [account.providerId],
    cities: sortedUnique(
      account.scopes.cities.length > 0
        ? account.scopes.cities
        : configurations.map((configuration) => configuration.city),
    ),
    operationalCapacities: sortedOperationalCapacities(configurations.map(operationalCapacity)),
  };
}

function metadataForApartmentConfiguration(
  configuration: ApartmentConfiguration,
): AdminPageFilterMetadata {
  return {
    owners: [configuration.owner],
    providers: providersForApartments([configuration]),
    cities: sortedUnique([configuration.city]),
    operationalCapacities: [operationalCapacity(configuration)],
  };
}

function metadataForBox(box: AdminBox): AdminPageFilterMetadata {
  const label = ADMIN_BOX_FILTER_LABEL_PATTERN.exec(box.displayName);
  const owner = label?.[1] ?? null;
  const streetNumber = label?.[2] ?? null;
  const assignedApartment = box.currentDeliveryAssignment?.apartmentName;
  const configurations = apartmentConfigurations.value.filter((configuration) =>
    assignedApartment
      ? configuration.apartment === assignedApartment
      : configuration.owner.toLocaleUpperCase('fr-FR') === owner &&
        configuration.streetNumber === streetNumber,
  );
  const estateEntries = operationalEstate.value.filter(
    (entry) => entry.physicalBoxId === box.boxId,
  );
  return {
    owners: sortedUnique([
      owner,
      ...configurations.map((configuration) => configuration.owner),
      ...estateEntries.map((entry) => entry.client),
    ]),
    providers: sortedUnique([
      ...providersForApartments(configurations),
      ...estateEntries.flatMap((entry) => entry.providerIds),
    ]),
    cities: sortedUnique([
      ...configurations.map((configuration) => configuration.city),
      ...estateEntries.map((entry) => entry.city),
    ]),
    operationalCapacities: sortedOperationalCapacities(configurations.map(operationalCapacity)),
  };
}

function matchesAdminPageFilters(
  metadata: AdminPageFilterMetadata,
  owner: string,
  provider: string,
  city: string,
  operationalCapacityFilter: string,
): boolean {
  return (
    (owner === 'ALL' || metadata.owners.includes(owner)) &&
    (provider === 'ALL' || metadata.providers.includes(provider)) &&
    (city === 'ALL' || metadata.cities.includes(city)) &&
    (operationalCapacityFilter === 'ALL' ||
      metadata.operationalCapacities.includes(operationalCapacityFilter))
  );
}

function filterOptions(metadata: AdminPageFilterMetadata[]): AdminPageFilterMetadata {
  return {
    owners: sortedUnique(metadata.flatMap((item) => item.owners)),
    providers: sortedUnique(metadata.flatMap((item) => item.providers)),
    cities: sortedUnique(metadata.flatMap((item) => item.cities)),
    operationalCapacities: sortedOperationalCapacities(
      metadata.flatMap((item) => item.operationalCapacities),
    ),
  };
}

const catalogFilterOptions = computed(() =>
  filterOptions(boxOffers.value.map((offer) => metadataForOffer(offer))),
);
const accountsFilterOptions = computed(() =>
  filterOptions([
    ...providerAccounts.value.map((account) => metadataForAccount(account)),
    ...apartmentConfigurations.value.map((configuration) =>
      metadataForApartmentConfiguration(configuration),
    ),
  ]),
);
const boxesFilterOptions = computed(() =>
  filterOptions(boxes.value.map((box) => metadataForBox(box))),
);
const filteredBoxOffers = computed(() =>
  boxOffers.value.filter((offer) =>
    matchesAdminPageFilters(
      metadataForOffer(offer),
      catalogOwnerFilter.value,
      catalogProviderFilter.value,
      catalogCityFilter.value,
      catalogOperationalCapacityFilter.value,
    ),
  ),
);
const filteredProviderAccounts = computed(() =>
  providerAccounts.value.filter((account) =>
    matchesAdminPageFilters(
      metadataForAccount(account),
      accountsOwnerFilter.value,
      accountsProviderFilter.value,
      accountsCityFilter.value,
      accountsOperationalCapacityFilter.value,
    ),
  ),
);
const filteredApartmentConfigurations = computed(() =>
  apartmentConfigurations.value.filter((configuration) =>
    matchesAdminPageFilters(
      metadataForApartmentConfiguration(configuration),
      accountsOwnerFilter.value,
      accountsProviderFilter.value,
      accountsCityFilter.value,
      accountsOperationalCapacityFilter.value,
    ),
  ),
);
const filteredBoxes = computed(() =>
  boxes.value.filter((box) =>
    matchesAdminPageFilters(
      metadataForBox(box),
      boxesOwnerFilter.value,
      boxesProviderFilter.value,
      boxesCityFilter.value,
      boxesOperationalCapacityFilter.value,
    ),
  ),
);
const damagedPhysicalBoxCount = computed(
  () =>
    boxes.value.filter((box) => ['THEFT', 'BREAKAGE'].includes(box.conditionState ?? 'ACTIVE'))
      .length,
);

const currentDeliveryRequests = computed(() =>
  deliveryRequests.value.filter(
    (request): request is CurrentDeliveryRequest =>
      request.deliveryRequestKind === 'CURRENT_LOGISTICS',
  ),
);
const activeDeliveryRequests = computed(() =>
  currentDeliveryRequests.value.filter(
    (request) => !['DELIVERED', 'CANCELLED'].includes(request.deliveryLifecycleState),
  ),
);
const exceptionDeliveryRequests = computed(() =>
  currentDeliveryRequests.value.filter((request) =>
    ['MODIFICATION_PROPOSEE', 'REFUSEE'].includes(request.deliveryLifecycleState),
  ),
);
const selectedUpsellMetrics = computed(
  () => upsellDashboard.value?.metricsByScope[dashboardScope.value] ?? null,
);
const dashboardPiePalette = ['#1473e6', '#32a852', '#f29b18', '#dc3545', '#7c5ce7'];
const dashboardPieSlices = computed(() => {
  const source = upsellDashboard.value?.pieSlicesByScope?.[dashboardScope.value] ?? [];
  const total = source.reduce((sum, item) => sum + item.valueMinor, 0);
  return source.map((item, index) => ({
    ...item,
    color: dashboardPiePalette[index % dashboardPiePalette.length],
    share: total > 0 ? item.valueMinor / total : 0,
  }));
});
const dashboardPieStyle = computed(() => {
  if (dashboardPieSlices.value.length === 0) return { '--pie-fill': '#dce5ef' };
  let cursor = 0;
  const stops = dashboardPieSlices.value.map((slice) => {
    const start = cursor;
    cursor += slice.share * 100;
    return `${slice.color} ${start}% ${cursor}%`;
  });
  return { '--pie-fill': `conic-gradient(${stops.join(', ')})` };
});
const dashboardPieCaption = computed(() =>
  dashboardScope.value.startsWith('APARTMENT:')
    ? 'Répartition du chiffre d’affaires par offre vendue'
    : 'Répartition du chiffre d’affaires par appartement',
);
type OperationalBadge = {
  key: string;
  apartment: string;
  apartmentLabel: string;
  city: string | null;
  client: string | null;
  offer: string;
  label: string;
  tone: 'to-prepare' | 'consumed' | 'delivered' | 'installed';
  providerIds: string[];
  alert: boolean;
  alertDetails: PurchaseAlert[];
  offerChangeAlert: ProviderOfferChangeAlert | null;
  boxDisplayName: string | null;
  minimumSaleCoefficient: number;
  temporaryPreparationName: string | null;
  slotNumber: number | null;
};

const allCleaningPriorityBadges = computed<OperationalBadge[]>(() => {
  const latestRequestByPosition = new Map<string, CurrentDeliveryRequest>();
  for (const request of [...currentDeliveryRequests.value].sort((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  )) {
    latestRequestByPosition.set(
      request.assignedBox?.boxId ?? `request:${request.deliveryRequestId}`,
      request,
    );
  }
  const fallbackEstate: OperationalEstateEntry[] = [...latestRequestByPosition.entries()].map(
    ([positionIdentity, request]) => {
      const physicalBox = boxes.value.find((box) => box.boxId === request.assignedBox?.boxId);
      const tone: OperationalEstateEntry['tone'] = !['DELIVERED', 'CANCELLED'].includes(
        request.deliveryLifecycleState,
      )
        ? 'to-prepare'
        : physicalBox?.travelerConsumed === true
          ? 'consumed'
          : request.configuredAssignment === true
            ? 'installed'
            : 'delivered';
      return {
        key: `position-${positionIdentity}`,
        apartment: request.apartmentName,
        city: null,
        client: null,
        providerIds: request.preparedByProviderId ? [request.preparedByProviderId] : [],
        deliveryRequestId: request.deliveryRequestId,
        physicalBoxId: request.assignedBox?.boxId ?? null,
        boxDisplayName: request.assignedBox?.displayName ?? null,
        offerId: request.selectedBoxOfferId,
        offer: request.selectedBoxOfferDisplayNameSnapshot,
        tone,
        label:
          tone === 'to-prepare'
            ? 'Préparer'
            : tone === 'consumed'
              ? 'Vendue'
              : tone === 'installed'
                ? 'Prête'
                : 'Commande',
        slotNumber: request.configuredAssignmentSlotNumber ?? null,
        requiresPhysicalBox: request.assignedBox === undefined,
        eligiblePhysicalBoxCount: request.availablePhysicalBoxes?.length ?? 0,
        purchaseAlerts: request.purchaseAlerts ?? [],
        offerChangeAlert: request.providerOfferChangeAlert ?? null,
      };
    },
  );
  const sourceEstate =
    operationalEstate.value.length > 0 ? operationalEstate.value : fallbackEstate;
  const badges = sourceEstate.map((entry) => {
    return {
      key: entry.key,
      apartment: entry.apartment,
      apartmentLabel: entry.apartment,
      city: entry.city,
      client: entry.client,
      offer: entry.offer ?? '',
      label: entry.label,
      tone: entry.tone,
      providerIds: entry.providerIds,
      alert:
        entry.purchaseAlerts.some((alert) => alert.status === 'OPEN') ||
        entry.offerChangeAlert?.status === 'OPEN',
      alertDetails: entry.purchaseAlerts.filter((alert) => alert.status === 'OPEN'),
      offerChangeAlert: entry.offerChangeAlert,
      boxDisplayName: entry.boxDisplayName,
      minimumSaleCoefficient:
        boxOffers.value.find((offer) => offer.offerId === entry.offerId)
          ?.minimumSaleCoefficient ?? 2,
      temporaryPreparationName: null,
      slotNumber: entry.slotNumber,
    };
  });
  const groups = new Map<string, OperationalBadge[]>();
  for (const badge of badges) {
    groups.set(badge.apartment, [...(groups.get(badge.apartment) ?? []), badge]);
  }
  for (const group of groups.values()) {
    if (group.length <= 1) continue;
    const occupied = new Set(
      group
        .map((badge) => badge.slotNumber)
        .filter(
          (slot): slot is number =>
            typeof slot === 'number' &&
            Number.isSafeInteger(slot) &&
            slot >= 1 &&
            slot <= group.length,
        ),
    );
    const freeSlots = Array.from({ length: group.length }, (_, index) => index + 1).filter(
      (slot) => !occupied.has(slot),
    );
    for (const badge of [...group].sort((left, right) => left.key.localeCompare(right.key))) {
      const slot = badge.slotNumber ?? freeSlots.shift() ?? 1;
      badge.apartmentLabel = `${badge.apartment} ${slot}/${group.length}`;
    }
  }
  return badges;
});
const operationalStatusOptions = computed(() =>
  [...new Set(allCleaningPriorityBadges.value.map((badge) => badge.tone))].sort(),
);
const operationalProviderOptions = computed(() =>
  [...new Set(allCleaningPriorityBadges.value.flatMap((badge) => badge.providerIds))].sort(
    (left, right) => left.localeCompare(right),
  ),
);
const operationalClientOptions = computed(() =>
  [
    ...new Set(
      allCleaningPriorityBadges.value
        .map((badge) => badge.client)
        .filter((client): client is string => Boolean(client)),
    ),
  ].sort((left, right) => left.localeCompare(right, 'fr')),
);
const operationalCityOptions = computed(() =>
  [
    ...new Set(
      allCleaningPriorityBadges.value
        .map((badge) => badge.city)
        .filter((city): city is string => Boolean(city)),
    ),
  ].sort((left, right) => left.localeCompare(right, 'fr')),
);
const operationalApartmentOptions = computed(() =>
  [...new Set(allCleaningPriorityBadges.value.map((badge) => badge.apartment))].sort(
    (left, right) => left.localeCompare(right, 'fr'),
  ),
);
const cleaningPriorityBadges = computed(() =>
  allCleaningPriorityBadges.value.filter(
    (badge) =>
      (operationalStatusFilter.value === 'ALL' ||
        badge.tone === operationalStatusFilter.value) &&
      (operationalProviderFilter.value === 'ALL' ||
        (operationalProviderFilter.value === 'UNASSIGNED'
          ? badge.providerIds.length === 0
          : badge.providerIds.includes(operationalProviderFilter.value))) &&
      (operationalClientFilter.value === 'ALL' ||
        badge.client === operationalClientFilter.value) &&
      (operationalCityFilter.value === 'ALL' || badge.city === operationalCityFilter.value) &&
      (operationalApartmentFilter.value === 'ALL' ||
        badge.apartment === operationalApartmentFilter.value),
  ),
);
const operationalConsumableNames = computed(() => [
  ...new Set(
    (operationalBanner.value?.consumableAlerts ?? []).map((alert) => alert.missingProductName),
  ),
]);
const operationalRecoveryProblem = computed(
  () =>
    (operationalBanner.value?.damageCount ?? 0) > 0 ||
    (operationalBanner.value?.theftCount ?? 0) > 0 ||
    (operationalBanner.value?.pendingRecoveryCount ?? 0) > 0,
);
const selectedQr = computed(() =>
  selected.value ? createPublicQrDataUrl(selected.value.qrUrl) : '',
);
const ADMIN_BOX_LABEL_PATTERN = /^Box_[1-9]\d*_[\p{Lu}][\p{Lu}'-]*_[1-9]\d*$/u;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isAdminBox(value: unknown): value is AdminBox {
  if (!isRecord(value)) return false;
  return (
    typeof value.boxId === 'string' &&
    typeof value.displayName === 'string' &&
    (value.offerId === undefined || typeof value.offerId === 'string') &&
    Number.isSafeInteger(value.amountMinor) &&
    value.currency === 'EUR' &&
    typeof value.priceVersion === 'string' &&
    typeof value.publicQrToken === 'string' &&
    typeof value.qrUrl === 'string' &&
    ['AVAILABLE', 'ASSIGNED_OR_IN_USE'].includes(String(value.availabilityState)) &&
    ['ACTIVE', 'THEFT', 'BREAKAGE'].includes(String(value.conditionState ?? 'ACTIVE')) &&
    (value.travelerConsumed === undefined || typeof value.travelerConsumed === 'boolean') &&
    (value.currentDeliveryRequestId === null ||
      typeof value.currentDeliveryRequestId === 'string') &&
    (value.currentDeliveryAssignment === undefined ||
      (isRecord(value.currentDeliveryAssignment) &&
        typeof value.currentDeliveryAssignment.deliveryRequestId === 'string' &&
        typeof value.currentDeliveryAssignment.requestedOfferId === 'string' &&
        (value.currentDeliveryAssignment.apartmentName === undefined ||
          typeof value.currentDeliveryAssignment.apartmentName === 'string')))
  );
}

function isBoxOffer(value: unknown): value is BoxOffer {
  if (!isRecord(value)) return false;
  return (
    typeof value.offerId === 'string' &&
    typeof value.displayName === 'string' &&
    Number.isSafeInteger(value.amountMinor) &&
    value.currency === 'EUR' &&
    typeof value.priceVersion === 'string' &&
    (value.products === undefined ||
      (Array.isArray(value.products) &&
        value.products.every(
          (product) =>
            isRecord(product) &&
            typeof product.name === 'string' &&
            Number.isSafeInteger(product.quantity) &&
            Number.isSafeInteger(product.purchasePriceMinor),
        ))) &&
    (value.totalPurchasePriceMinor === undefined ||
      Number.isSafeInteger(value.totalPurchasePriceMinor)) &&
    (value.recommendedSalePriceMinor === undefined ||
      value.recommendedSalePriceMinor === null ||
      Number.isSafeInteger(value.recommendedSalePriceMinor)) &&
    (value.recommendedMarginPolicy === undefined ||
      ['INCONNU_NO_APPLICABLE_AUTHORITY', 'ESTIMATED_DEFAULT_PURCHASE_PRICE_X_2_0'].includes(
        String(value.recommendedMarginPolicy),
      )) &&
    (value.recommendedSalePriceSource === undefined ||
      ['ESTIMATED_DEFAULT', 'ADMIN_OVERRIDE'].includes(
        String(value.recommendedSalePriceSource),
      )) &&
    (value.minimumSaleCoefficient === undefined ||
      (typeof value.minimumSaleCoefficient === 'number' &&
        Number.isFinite(value.minimumSaleCoefficient))) &&
    (value.minimumSalePriceMinor === undefined ||
      Number.isSafeInteger(value.minimumSalePriceMinor)) &&
    (value.photoDataUrl === undefined || typeof value.photoDataUrl === 'string')
  );
}

function isProviderOfferChangeAlert(value: unknown): value is ProviderOfferChangeAlert {
  return (
    isRecord(value) &&
    typeof value.providerId === 'string' &&
    typeof value.previousOfferId === 'string' &&
    typeof value.previousOfferDisplayName === 'string' &&
    typeof value.newOfferId === 'string' &&
    typeof value.newOfferDisplayName === 'string' &&
    typeof value.changedAt === 'string' &&
    value.status === 'OPEN'
  );
}

function isProviderException(value: unknown): value is ProviderException {
  return (
    isRecord(value) &&
    typeof value.providerId === 'string' &&
    (value.proposedAlternative === null || typeof value.proposedAlternative === 'string') &&
    (value.refusalReason === null || typeof value.refusalReason === 'string') &&
    typeof value.reportedAt === 'string'
  );
}

function isCurrentDeliveryRequest(value: Record<string, unknown>): boolean {
  return (
    value.deliveryRequestKind === 'CURRENT_LOGISTICS' &&
    typeof value.deliveryRequestId === 'string' &&
    [
      'RECEIVED',
      'MODIFICATION_PROPOSEE',
      'REFUSEE',
      'PREPARING',
      'READY',
      'DELIVERED',
      'CANCELLED',
    ].includes(String(value.deliveryLifecycleState)) &&
    typeof value.selectedBoxOfferId === 'string' &&
    typeof value.selectedBoxOfferDisplayNameSnapshot === 'string' &&
    Number.isSafeInteger(value.selectedBoxOfferAmountMinorSnapshot) &&
    value.selectedBoxOfferCurrencySnapshot === 'EUR' &&
    typeof value.selectedBoxOfferPriceVersionSnapshot === 'string' &&
    (value.preparedBoxOfferId === undefined ||
      value.preparedBoxOfferId === null ||
      typeof value.preparedBoxOfferId === 'string') &&
    (value.preparedByProviderId === undefined ||
      value.preparedByProviderId === null ||
      typeof value.preparedByProviderId === 'string') &&
    (value.purchaseAlerts === undefined || Array.isArray(value.purchaseAlerts)) &&
    (value.providerOfferChangeAlert === undefined ||
      isProviderOfferChangeAlert(value.providerOfferChangeAlert)) &&
    (value.temporaryPreparation === undefined || isRecord(value.temporaryPreparation)) &&
    (value.travelerOrder === undefined || typeof value.travelerOrder === 'boolean') &&
    typeof value.createdAt === 'string' &&
    typeof value.travelerName === 'string' &&
    typeof value.apartmentName === 'string' &&
    typeof value.deliveryDeadlineAt === 'string' &&
    value.absentEntryAuthorized === true &&
    (value.providerException === null || isProviderException(value.providerException)) &&
    (value.sourceBoxId === null || typeof value.sourceBoxId === 'string') &&
    typeof value.sourceBoxDisplayName === 'string' &&
    (value.assignedBox === undefined || isAdminBox(value.assignedBox)) &&
    (value.availablePhysicalBoxes === undefined ||
      (Array.isArray(value.availablePhysicalBoxes) &&
        value.availablePhysicalBoxes.every(isAdminBox)))
  );
}

function formatPrice(value: { amountMinor: number; currency: 'EUR' }): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: value.currency,
  }).format(value.amountMinor / 100);
}

function formatDeliveryDeadline(value: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function amountMinorFrom(value: string): number {
  return Math.round(Number(value.replace(',', '.')) * 100);
}

function boxAvailabilityLabel(box: AdminBox): string {
  if (box.conditionState === 'THEFT') return 'Vol';
  if (box.conditionState === 'BREAKAGE') return 'Casse';
  if (box.availabilityState === 'AVAILABLE') return 'Disponible';
  const apartmentName = box.currentDeliveryAssignment?.apartmentName;
  return typeof apartmentName === 'string' && apartmentName.trim()
    ? apartmentName.trim()
    : 'Logement non renseigné · données à vérifier';
}

function isCanonicalAdminBoxLabel(value: string): boolean {
  return ADMIN_BOX_LABEL_PATTERN.test(value.trim());
}

function adminBoxLabel(box: AdminBox): string {
  return isCanonicalAdminBoxLabel(box.displayName) ? box.displayName.trim() : 'Box à renommer';
}

async function responseError(
  response: Awaited<ReturnType<typeof globalThis.fetch>>,
  fallback: string,
): Promise<Error> {
  try {
    const body: unknown = await response.json();
    if (isRecord(body) && typeof body.error === 'string') {
      const safeMessages: Record<string, string> = {
        PREPARED_BOX_OFFER_MISMATCH:
          'L’offre réellement préparée ne correspond pas à l’offre demandée.',
        PREPARATION_OFFER_MATCH_REQUIRED:
          'La livraison reste bloquée tant que l’offre préparée n’est pas confirmée.',
      };
      return new Error(safeMessages[body.error] ?? body.error);
    }
  } catch {
    // Preserve a controlled fallback for invalid error responses.
  }
  return new Error(fallback);
}

async function loadBoxes(): Promise<void> {
  boxesLoading.value = true;
  boxesError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/boxes', { credentials: 'include' });
    const body: unknown = await response.json();
    if (
      !response.ok ||
      !isRecord(body) ||
      !Array.isArray(body.boxes) ||
      !body.boxes.every(isAdminBox)
    ) {
      throw new Error('BOX_LIST_INVALID');
    }
    boxes.value = body.boxes;
    selected.value = selected.value
      ? (boxes.value.find((box) => box.boxId === selected.value?.boxId) ?? null)
      : (boxes.value[0] ?? null);
  } catch {
    boxesError.value = 'Impossible de charger les Boxes.';
  } finally {
    boxesLoading.value = false;
  }
}

async function loadBoxLockCodes(): Promise<void> {
  try {
    const response = await globalThis.fetch('/admin/api/box-lock-codes', {
      credentials: 'include',
    });
    const body: unknown = await response.json();
    if (
      !response.ok ||
      !isRecord(body) ||
      !Array.isArray(body.boxLockCodes) ||
      !body.boxLockCodes.every(
        (entry): entry is AdminBoxLockCode =>
          isRecord(entry) &&
          typeof entry.boxId === 'string' &&
          (entry.lockCode === null ||
            (typeof entry.lockCode === 'string' && /^\d{3}$/.test(entry.lockCode))) &&
          (entry.codeLastChangedAt === undefined ||
            entry.codeLastChangedAt === null ||
            typeof entry.codeLastChangedAt === 'string') &&
          (entry.ageDays === undefined ||
            entry.ageDays === null ||
            Number.isSafeInteger(entry.ageDays)) &&
          (entry.renewalDue === undefined || typeof entry.renewalDue === 'boolean') &&
          (entry.renewalDays === undefined ||
            entry.renewalDays === null ||
            Number.isSafeInteger(entry.renewalDays)),
      )
    ) {
      throw new Error('BOX_LOCK_CODE_LIST_INVALID');
    }
    boxLockCodes.value = Object.fromEntries(
      body.boxLockCodes.map((entry) => [entry.boxId, entry.lockCode]),
    );
    boxLockCodeEntries.value = Object.fromEntries(
      body.boxLockCodes.map((entry) => [entry.boxId, entry]),
    );
  } catch {
    boxLockCodes.value = {};
    boxLockCodeEntries.value = {};
  }
}

async function loadOffers(): Promise<void> {
  offersLoading.value = true;
  offersError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/box-offers', {
      credentials: 'include',
    });
    const body: unknown = await response.json();
    if (
      !response.ok ||
      !isRecord(body) ||
      !Array.isArray(body.boxOffers) ||
      !body.boxOffers.every(isBoxOffer)
    ) {
      throw new Error('BOX_OFFER_LIST_INVALID');
    }
    boxOffers.value = body.boxOffers;
  } catch {
    offersError.value = 'Impossible de charger les offres de Box.';
  } finally {
    offersLoading.value = false;
  }
}

async function loadDeliveryRequests(): Promise<void> {
  deliveryLoading.value = true;
  deliveryError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/delivery-requests', {
      credentials: 'include',
    });
    const body: unknown = await response.json();
    if (!response.ok || !isRecord(body) || !Array.isArray(body.deliveryRequests)) {
      throw new Error('DELIVERY_QUEUE_INVALID');
    }
    const currentRecords = body.deliveryRequests.filter(
      (request) => isRecord(request) && request.deliveryRequestKind === 'CURRENT_LOGISTICS',
    );
    if (!currentRecords.every(isCurrentDeliveryRequest)) {
      throw new Error('DELIVERY_QUEUE_INVALID');
    }
    deliveryRequests.value = currentRecords.filter(isCurrentDeliveryRequest);
  } catch {
    deliveryError.value = 'Impossible de charger les commandes à livrer.';
  } finally {
    deliveryLoading.value = false;
  }
}

function isUpsellDashboard(value: unknown): value is UpsellDashboard {
  return (
    isRecord(value) &&
    Array.isArray(value.filters) &&
    isRecord(value.metricsByScope) &&
    (value.offerChangesByScope === undefined ||
      (isRecord(value.offerChangesByScope) &&
        Object.values(value.offerChangesByScope).every(isOfferChangeSummary))) &&
    (value.pieSlicesByScope === undefined || isRecord(value.pieSlicesByScope)) &&
    (value.selectedOfferId === undefined || typeof value.selectedOfferId === 'string') &&
    (value.selectedSaleMode === undefined ||
      ['ALL', 'ORDERED', 'SELF_SERVICE'].includes(String(value.selectedSaleMode))) &&
    (value.offerFilters === undefined ||
      (Array.isArray(value.offerFilters) &&
        value.offerFilters.every(
          (filter) =>
            isRecord(filter) &&
            typeof filter.key === 'string' &&
            typeof filter.label === 'string',
        ))) &&
    (value.saleModeFilters === undefined ||
      (Array.isArray(value.saleModeFilters) &&
        value.saleModeFilters.every(
          (filter) =>
            isRecord(filter) &&
            ['ALL', 'ORDERED', 'SELF_SERVICE'].includes(String(filter.key)) &&
            typeof filter.label === 'string',
        ))) &&
    value.filters.every(
      (filter) =>
        isRecord(filter) &&
        typeof filter.key === 'string' &&
        ['ALL', 'APARTMENT', 'OWNER'].includes(String(filter.kind)) &&
        typeof filter.label === 'string',
    )
  );
}

async function addProviderScopeValue(dimension: keyof ProviderScopes): Promise<void> {
  const value = providerScopeDrafts.value[dimension][0]?.trim() ?? '';
  if (!value) return;
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/provider-scope-values', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ dimension, value }),
    });
    if (!response.ok) throw new Error('PROVIDER_SCOPE_VALUE_SAVE_FAILED');
    providerScopeDrafts.value[dimension] = [];
    await loadProviderAccounts();
  } catch {
    actionError.value = 'Impossible d’enregistrer cette valeur de périmètre.';
  } finally {
    saving.value = false;
  }
}

async function loadUpsellDashboard(): Promise<void> {
  const version = ++dashboardLoadVersion;
  dashboardLoading.value = true;
  dashboardError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/upsell-dashboard?period=${encodeURIComponent(dashboardPeriod.value)}` +
        `&offerId=${encodeURIComponent(dashboardOfferId.value)}` +
        `&saleMode=${encodeURIComponent(dashboardSaleMode.value)}`,
      {
        credentials: 'include',
      },
    );
    const body: unknown = await response.json();
    if (version !== dashboardLoadVersion) return;
    if (!response.ok || !isUpsellDashboard(body)) throw new Error('UPSELL_DASHBOARD_INVALID');
    upsellDashboard.value = body;
    if (
      body.offerFilters &&
      !body.offerFilters.some((filter) => filter.key === dashboardOfferId.value)
    ) {
      dashboardOfferId.value = 'ALL';
    }
    if (!body.metricsByScope[dashboardScope.value]) dashboardScope.value = 'ALL';
  } catch {
    if (version === dashboardLoadVersion)
      dashboardError.value = 'Indicateurs upsells indisponibles.';
  } finally {
    if (version === dashboardLoadVersion) dashboardLoading.value = false;
  }
}

function isProviderScopes(value: unknown): value is ProviderScopes {
  return (
    isRecord(value) &&
    ['cities', 'apartments', 'owners'].every(
      (dimension) =>
        Array.isArray(value[dimension]) &&
        value[dimension].every((item) => typeof item === 'string'),
    )
  );
}

function isProviderAccount(value: unknown): value is ProviderAccount {
  return (
    isRecord(value) &&
    typeof value.providerId === 'string' &&
    typeof value.username === 'string' &&
    typeof value.active === 'boolean' &&
    isProviderScopes(value.scopes) &&
    typeof value.createdAt === 'string' &&
    typeof value.updatedAt === 'string'
  );
}

async function loadProviderAccounts(): Promise<void> {
  providerAccountsLoading.value = true;
  try {
    const response = await globalThis.fetch('/admin/api/provider-accounts', {
      credentials: 'include',
    });
    const body: unknown = await response.json();
    if (
      !response.ok ||
      !isRecord(body) ||
      !Array.isArray(body.providerAccounts) ||
      !body.providerAccounts.every(isProviderAccount) ||
      !isProviderScopes(body.scopeOptions)
    ) {
      throw new Error('PROVIDER_ACCOUNTS_INVALID');
    }
    providerAccounts.value = body.providerAccounts;
    providerScopeOptions.value = body.scopeOptions;
  } catch {
    actionError.value = 'Impossible de charger les comptes prestataires.';
  } finally {
    providerAccountsLoading.value = false;
  }
}

function isApartmentConfiguration(value: unknown): value is ApartmentConfiguration {
  return (
    isRecord(value) &&
    typeof value.apartment === 'string' &&
    (value.city === undefined || value.city === null || typeof value.city === 'string') &&
    typeof value.owner === 'string' &&
    (value.streetNumber === undefined ||
      value.streetNumber === null ||
      typeof value.streetNumber === 'string') &&
    (value.listingPhotoDataUrl === undefined ||
      typeof value.listingPhotoDataUrl === 'string') &&
    Array.isArray(value.offerIds) &&
    value.offerIds.every((offerId) => typeof offerId === 'string') &&
    Number.isSafeInteger(value.activeBoxTarget) &&
    Number.isSafeInteger(value.activeBoxCount) &&
    typeof value.updatedAt === 'string' &&
    Array.isArray(value.offers)
  );
}

function isOperationalEstateEntry(value: unknown): value is OperationalEstateEntry {
  return (
    isRecord(value) &&
    typeof value.key === 'string' &&
    typeof value.apartment === 'string' &&
    (value.city === null || typeof value.city === 'string') &&
    (value.client === null || typeof value.client === 'string') &&
    Array.isArray(value.providerIds) &&
    value.providerIds.every((provider) => typeof provider === 'string') &&
    (value.deliveryRequestId === null || typeof value.deliveryRequestId === 'string') &&
    (value.physicalBoxId === null || typeof value.physicalBoxId === 'string') &&
    (value.boxDisplayName === null || typeof value.boxDisplayName === 'string') &&
    (value.offerId === null || typeof value.offerId === 'string') &&
    (value.offer === null || typeof value.offer === 'string') &&
    ['to-prepare', 'consumed', 'delivered', 'installed'].includes(String(value.tone)) &&
    ['Préparer', 'Vendue', 'Commande', 'Prête'].includes(String(value.label)) &&
    (value.slotNumber === null || Number.isSafeInteger(value.slotNumber)) &&
    typeof value.requiresPhysicalBox === 'boolean' &&
    Number.isSafeInteger(value.eligiblePhysicalBoxCount) &&
    Array.isArray(value.purchaseAlerts) &&
    (value.offerChangeAlert === null || isProviderOfferChangeAlert(value.offerChangeAlert))
  );
}

async function loadOperationalEstate(): Promise<void> {
  operationalEstateLoading.value = true;
  operationalEstateError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/operational-estate', {
      credentials: 'include',
    });
    const body: unknown = await response.json();
    if (
      !response.ok ||
      !isRecord(body) ||
      !Array.isArray(body.entries) ||
      !body.entries.every(isOperationalEstateEntry) ||
      !isRecord(body.banner) ||
      !Array.isArray(body.banner.consumableAlerts) ||
      !Number.isSafeInteger(body.banner.missingBoxCount) ||
      !Array.isArray(body.banner.noEligibleAlerts) ||
      !Number.isSafeInteger(body.banner.pendingRecoveryCount) ||
      !Number.isSafeInteger(body.banner.damageCount) ||
      !Number.isSafeInteger(body.banner.theftCount)
    ) {
      throw new Error('OPERATIONAL_ESTATE_INVALID');
    }
    operationalEstate.value = body.entries;
    operationalBanner.value = body.banner as OperationalBanner;
  } catch {
    operationalEstateError.value = 'État global des appartements indisponible.';
  } finally {
    operationalEstateLoading.value = false;
  }
}

async function loadApartmentConfigurations(): Promise<void> {
  try {
    const response = await globalThis.fetch('/admin/api/apartment-configurations', {
      credentials: 'include',
    });
    const body: unknown = await response.json();
    if (
      !response.ok ||
      !isRecord(body) ||
      !Array.isArray(body.apartmentConfigurations) ||
      !body.apartmentConfigurations.every(isApartmentConfiguration)
    ) {
      throw new Error('APARTMENT_CONFIGURATIONS_INVALID');
    }
    apartmentConfigurations.value = body.apartmentConfigurations;
  } catch {
    actionError.value = 'Impossible de charger la configuration des appartements.';
  }
}

async function saveApartmentConfiguration(): Promise<void> {
  const draft = apartmentConfigurationDraft.value;
  if (
    !draft.apartment ||
    !draft.city ||
    !draft.owner ||
    !/^[1-9]\d{0,5}[A-Za-z]?$/.test(draft.streetNumber) ||
    draft.offerIds.length === 0 ||
    !Number.isSafeInteger(draft.activeBoxTarget) ||
    draft.activeBoxTarget < 1
  ) {
    actionError.value =
      'Appartement, ville, propriétaire, numéro de rue, offres et capacité cohérente sont requis.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/apartment-configurations', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(draft),
    });
    if (!response.ok) throw await responseError(response, 'Configuration refusée.');
    apartmentConfigurationDraft.value = {
      apartment: '',
      city: '',
      owner: '',
      streetNumber: '',
      offerIds: [],
      activeBoxTarget: 1,
    };
    apartmentConfigurationFormOpen.value = false;
    await Promise.all([loadApartmentConfigurations(), loadProviderAccounts()]);
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Configuration refusée.';
  } finally {
    saving.value = false;
  }
}

function beginApartmentConfiguration(configuration?: ApartmentConfiguration): void {
  if (configuration) {
    apartmentConfigurationDraft.value = {
      apartment: configuration.apartment,
      city: configuration.city ?? '',
      owner: configuration.owner,
      streetNumber: configuration.streetNumber ?? '',
      offerIds: [...configuration.offerIds],
      activeBoxTarget: configuration.activeBoxTarget,
    };
  }
  apartmentConfigurationFormOpen.value = true;
}

async function deleteApartmentConfiguration(
  configuration: ApartmentConfiguration,
): Promise<void> {
  if (!globalThis.confirm(`Supprimer la configuration de ${configuration.apartment} ?`)) return;
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/apartment-configurations/${encodeURIComponent(configuration.apartment)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    if (!response.ok) throw await responseError(response, 'Suppression refusée.');
    await loadApartmentConfigurations();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Suppression refusée.';
  } finally {
    saving.value = false;
  }
}

function cancelApartmentConfiguration(): void {
  apartmentConfigurationFormOpen.value = false;
  apartmentConfigurationDraft.value = {
    apartment: '',
    city: '',
    owner: '',
    streetNumber: '',
    offerIds: [],
    activeBoxTarget: 1,
  };
}

async function loadAdminData(): Promise<void> {
  if (props.managementMode) {
    await Promise.all([
      loadBoxes(),
      loadOffers(),
      loadProviderAccounts(),
      loadApartmentConfigurations(),
    ]);
    return;
  }
  if (props.section === 'catalog') {
    await Promise.all([
      loadBoxes(),
      loadOffers(),
      loadDeliveryRequests(),
      loadProviderAccounts(),
      loadApartmentConfigurations(),
    ]);
    return;
  }
  if (props.section === 'boxes') {
    await Promise.all([
      loadBoxes(),
      loadBoxLockCodes(),
      loadProviderAccounts(),
      loadApartmentConfigurations(),
      loadOperationalEstate(),
    ]);
    return;
  }
  await Promise.all([
    loadBoxes(),
    loadBoxLockCodes(),
    loadOffers(),
    loadDeliveryRequests(),
    loadUpsellDashboard(),
    loadOperationalEstate(),
  ]);
}

function resetProviderDraft(): void {
  providerScopeError.value = '';
  editingProviderId.value = '';
  providerDraft.value = {
    providerId: '',
    username: '',
    password: '',
    scopes: { cities: [], apartments: [], owners: [] },
  };
  providerFormOpen.value = false;
}

function beginProviderCreate(): void {
  resetProviderDraft();
  providerFormOpen.value = true;
}

function beginProviderEdit(account: ProviderAccount): void {
  providerScopeError.value = '';
  editingProviderId.value = account.providerId;
  providerDraft.value = {
    providerId: account.providerId,
    username: account.username,
    password: '',
    scopes: {
      cities: [...account.scopes.cities],
      apartments: [...account.scopes.apartments],
      owners: [...account.scopes.owners],
    },
  };
  providerFormOpen.value = true;
}

async function saveProviderAccount(): Promise<void> {
  providerScopeError.value = '';
  if (
    !providerDraft.value.providerId.trim() ||
    !providerDraft.value.username.trim() ||
    (!editingProviderId.value && providerDraft.value.password.length < 8)
  ) {
    actionError.value = 'Identifiant, utilisateur et mot de passe initial sont requis.';
    return;
  }
  if (
    providerDraft.value.scopes.cities.length === 0 &&
    providerDraft.value.scopes.apartments.length === 0
  ) {
    providerScopeError.value =
      'Sélectionnez au moins une ville ou un appartement pour limiter les accès.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const editing = Boolean(editingProviderId.value);
    const inferredOwners = [
      ...new Set(
        apartmentConfigurations.value
          .filter((configuration) =>
            providerDraft.value.scopes.apartments.includes(configuration.apartment),
          )
          .map((configuration) => configuration.owner),
      ),
    ].sort((left, right) => left.localeCompare(right, 'fr'));
    const inferredScopes: ProviderScopes = {
      cities: [...providerDraft.value.scopes.cities],
      apartments: [...providerDraft.value.scopes.apartments],
      owners: inferredOwners,
    };
    const response = await globalThis.fetch(
      editing
        ? `/admin/api/provider-accounts/${encodeURIComponent(editingProviderId.value)}`
        : '/admin/api/provider-accounts',
      {
        method: editing ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          editing
            ? {
                username: providerDraft.value.username.trim(),
                password: providerDraft.value.password || null,
                scopes: inferredScopes,
              }
            : {
                providerId: providerDraft.value.providerId.trim(),
                username: providerDraft.value.username.trim(),
                password: providerDraft.value.password,
                scopes: inferredScopes,
              },
        ),
      },
    );
    if (!response.ok) throw await responseError(response, 'Compte prestataire refusé.');
    resetProviderDraft();
    await loadProviderAccounts();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Compte prestataire refusé.';
  } finally {
    saving.value = false;
  }
}

async function disableProviderAccount(account: ProviderAccount): Promise<void> {
  if (!globalThis.confirm(`Supprimer le compte ${account.providerId} ?`)) return;
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/provider-accounts/${encodeURIComponent(account.providerId)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    if (!response.ok) throw await responseError(response, 'Désactivation refusée.');
    await loadProviderAccounts();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Désactivation refusée.';
  } finally {
    saving.value = false;
  }
}

async function createOffer(): Promise<void> {
  const amountMinor = amountMinorFrom(newOfferAmountEuros.value);
  if (
    !newOfferDisplayName.value.trim() ||
    !Number.isSafeInteger(amountMinor) ||
    amountMinor < 1
  ) {
    actionError.value = 'Saisissez un nom et un prix EUR valides.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/box-offers', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        displayName: newOfferDisplayName.value.trim(),
        amountMinor,
      }),
    });
    if (!response.ok) throw await responseError(response, 'Création de l’offre refusée.');
    const created: unknown = await response.json();
    if (!isBoxOffer(created)) throw new Error('Offre créée invalide.');
    newOfferDisplayName.value = '';
    newOfferAmountEuros.value = '';
    await loadOffers();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Création impossible.';
  } finally {
    saving.value = false;
  }
}

async function duplicateOffer(offer: BoxOffer): Promise<void> {
  saving.value = true;
  actionError.value = '';
  actionSuccess.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/box-offers/${encodeURIComponent(offer.offerId)}/duplicate`,
      { method: 'POST', credentials: 'include' },
    );
    const duplicated: unknown = await response.json();
    if (!response.ok || !isBoxOffer(duplicated)) {
      throw new Error('Duplication de l’offre refusée.');
    }
    await loadOffers();
    actionSuccess.value = `${duplicated.displayName} est une copie indépendante et modifiable.`;
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Duplication impossible.';
  } finally {
    saving.value = false;
  }
}

function beginOfferPriceEdit(offer: BoxOffer): void {
  editingOfferNameId.value = '';
  editingOfferProductsId.value = '';
  editingOfferCoefficientId.value = '';
  editingOfferPriceId.value = offer.offerId;
  offerAmountEuros.value = (offer.amountMinor / 100).toFixed(2);
}

function beginOfferNameEdit(offer: BoxOffer): void {
  editingOfferPriceId.value = '';
  editingOfferProductsId.value = '';
  editingOfferCoefficientId.value = '';
  editingOfferNameId.value = offer.offerId;
  offerDisplayName.value = offer.displayName;
}

function beginOfferProductsEdit(offer: BoxOffer): void {
  editingOfferPriceId.value = '';
  editingOfferNameId.value = '';
  editingOfferCoefficientId.value = '';
  editingOfferProductsId.value = offer.offerId;
  offerProductsDraft.value = (offer.products ?? []).map((product) => ({
    name: product.name,
    quantity: String(product.quantity),
    purchasePriceEuros: (product.purchasePriceMinor / 100).toFixed(2),
  }));
}

function beginOfferCoefficientEdit(offer: BoxOffer): void {
  editingOfferPriceId.value = '';
  editingOfferNameId.value = '';
  editingOfferProductsId.value = '';
  editingOfferCoefficientId.value = offer.offerId;
  offerCoefficient.value = String(offer.minimumSaleCoefficient ?? 2);
}

async function saveMinimumCoefficient(offer: BoxOffer): Promise<void> {
  const coefficient = Number(offerCoefficient.value.replace(',', '.'));
  if (!Number.isFinite(coefficient) || coefficient < 0.1 || coefficient > 100) {
    actionError.value = 'Saisissez un coefficient valide.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/box-offers/${encodeURIComponent(offer.offerId)}/minimum-coefficient`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ minimumSaleCoefficient: coefficient }),
      },
    );
    if (!response.ok) throw await responseError(response, 'Coefficient refusé.');
    editingOfferCoefficientId.value = '';
    await loadOffers();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Modification impossible.';
  } finally {
    saving.value = false;
  }
}

function addOfferProduct(): void {
  offerProductsDraft.value.push({ name: '', quantity: '1', purchasePriceEuros: '0.00' });
}

function removeOfferProduct(index: number): void {
  offerProductsDraft.value.splice(index, 1);
}

async function saveOfferProducts(offer: BoxOffer): Promise<void> {
  const products = offerProductsDraft.value.map((product) => ({
    name: product.name.trim(),
    quantity: Number(product.quantity),
    purchasePriceMinor: amountMinorFrom(product.purchasePriceEuros),
  }));
  if (
    products.some(
      (product) =>
        !product.name ||
        !Number.isSafeInteger(product.quantity) ||
        product.quantity < 0 ||
        !Number.isSafeInteger(product.purchasePriceMinor) ||
        product.purchasePriceMinor < 0,
    )
  ) {
    actionError.value = 'Saisissez des produits, quantités et prix d’achat valides.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/box-offers/${encodeURIComponent(offer.offerId)}/products`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ products }),
      },
    );
    if (!response.ok) throw await responseError(response, 'Composition de l’offre refusée.');
    editingOfferProductsId.value = '';
    await loadOffers();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Modification impossible.';
  } finally {
    saving.value = false;
  }
}

function readOfferPhoto(file: globalThis.Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new globalThis.FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Photo illisible.'));
    });
    reader.addEventListener('error', () => reject(new Error('Photo illisible.')));
    reader.readAsDataURL(file);
  });
}

async function persistOfferPhoto(offer: BoxOffer, photoDataUrl: string | null): Promise<void> {
  saving.value = true;
  actionError.value = '';
  actionSuccess.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/box-offers/${encodeURIComponent(offer.offerId)}/photo`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ photoDataUrl }),
      },
    );
    const updated: unknown = await response.json();
    if (!response.ok || !isBoxOffer(updated)) {
      throw new Error('Enregistrement de la photo refusé.');
    }
    await loadOffers();
    actionSuccess.value =
      photoDataUrl === null
        ? `Photo retirée pour ${offer.displayName}.`
        : `Photo enregistrée pour ${offer.displayName}.`;
  } catch (cause) {
    actionError.value =
      cause instanceof Error ? cause.message : 'Enregistrement de la photo impossible.';
  } finally {
    saving.value = false;
  }
}

async function uploadOfferPhoto(
  offer: BoxOffer,
  event: { currentTarget: unknown },
): Promise<void> {
  const input = event.currentTarget as {
    files: { 0?: globalThis.Blob } | null;
    value: string;
  };
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    actionError.value = 'Choisissez une photo JPEG, PNG ou WebP.';
    return;
  }
  if (file.size < 1 || file.size > 512 * 1024) {
    actionError.value = 'La photo doit peser 512 Ko maximum.';
    return;
  }
  try {
    await persistOfferPhoto(offer, await readOfferPhoto(file));
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Photo illisible.';
  }
}

async function saveOffer(offer: BoxOffer, field: 'price' | 'display-name'): Promise<void> {
  const body =
    field === 'price'
      ? { amountMinor: amountMinorFrom(offerAmountEuros.value) }
      : { displayName: offerDisplayName.value.trim() };
  if (
    (field === 'price' &&
      (!Number.isSafeInteger(body.amountMinor) || Number(body.amountMinor) < 1)) ||
    (field === 'display-name' && !body.displayName)
  ) {
    actionError.value = 'Saisissez une valeur valide.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/box-offers/${encodeURIComponent(offer.offerId)}/${field}`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) throw await responseError(response, 'Modification de l’offre refusée.');
    editingOfferPriceId.value = '';
    editingOfferNameId.value = '';
    await loadOffers();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Modification impossible.';
  } finally {
    saving.value = false;
  }
}

async function createBox(): Promise<void> {
  const number = newBoxNumber.value.trim();
  const owner = newBoxOwner.value.trim().toLocaleUpperCase('fr-FR');
  const addressNumber = inferredBoxStreetNumber.value;
  const composedDisplayName = `Box_${number}_${owner}_${addressNumber}`;
  if (mixedBoxStreetNumbers.value) {
    actionError.value =
      'Les appartements sélectionnés doivent avoir le même numéro de rue pour créer cette Box.';
    return;
  }
  if (
    !/^[1-9]\d*$/.test(number) ||
    newBoxApartments.value.length === 0 ||
    !/^[\p{Lu}][\p{Lu}'-]*$/u.test(owner) ||
    !/^[1-9]\d*$/.test(addressNumber) ||
    !isCanonicalAdminBoxLabel(composedDisplayName) ||
    !/^\d{3}$/.test(newLockCode.value)
  ) {
    actionError.value =
      'Sélectionnez un propriétaire, au moins un appartement compatible et un code de 3 chiffres.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/boxes', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        displayName: composedDisplayName,
        lockCode: newLockCode.value,
        owner,
        apartmentNames: [...newBoxApartments.value],
      }),
    });
    if (!response.ok) throw await responseError(response, 'Création de la Box refusée.');
    const created: unknown = await response.json();
    if (!isAdminBox(created)) throw new Error('Box créée invalide.');
    newBoxNumber.value = '';
    newBoxOwner.value = '';
    newBoxApartments.value = [];
    newLockCode.value = '000';
    selected.value = created;
    await Promise.all([loadBoxes(), loadBoxLockCodes()]);
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Création impossible.';
  } finally {
    saving.value = false;
  }
}

function beginNameEdit(box: AdminBox): void {
  editingNameBoxId.value = box.boxId;
  displayName.value = box.displayName;
}

function beginLockCodeEdit(box: AdminBox): void {
  editingNameBoxId.value = '';
  editingLockCodeBoxId.value = box.boxId;
  lockCodeDraft.value = boxLockCodes.value[box.boxId] ?? '';
}

async function saveLockCode(box: AdminBox): Promise<void> {
  if (!/^\d{3}$/.test(lockCodeDraft.value)) {
    actionError.value = 'Le code doit contenir exactement 3 chiffres.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/boxes/${encodeURIComponent(box.boxId)}/lock-code`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ lockCode: lockCodeDraft.value }),
      },
    );
    if (!response.ok) throw await responseError(response, 'Changement du code refusé.');
    editingLockCodeBoxId.value = '';
    await loadBoxLockCodes();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Changement impossible.';
  } finally {
    saving.value = false;
  }
}

async function saveBox(box: AdminBox): Promise<void> {
  const body = { displayName: displayName.value.trim() };
  if (!isCanonicalAdminBoxLabel(body.displayName)) {
    actionError.value = 'Utilisez le format Box_<NUMÉRO>_<PROPRIÉTAIRE>_<NUMÉRO ADRESSE>.';
    return;
  }
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/boxes/${encodeURIComponent(box.boxId)}/display-name`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (!response.ok) throw await responseError(response, 'Modification de la Box refusée.');
    editingNameBoxId.value = '';
    await loadBoxes();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Modification impossible.';
  } finally {
    saving.value = false;
  }
}

async function deleteBox(box: AdminBox): Promise<void> {
  const damaged = ['THEFT', 'BREAKAGE'].includes(box.conditionState ?? 'ACTIVE');
  const prompt = damaged
    ? `Retirer ${adminBoxLabel(box)} du parc physique actif tout en conservant son historique ?`
    : `Supprimer définitivement ${adminBoxLabel(box)} ?`;
  if (!globalThis.confirm(prompt)) return;
  saving.value = true;
  actionError.value = '';
  try {
    const response = await globalThis.fetch(
      `/admin/api/boxes/${encodeURIComponent(box.boxId)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    if (!response.ok) {
      throw await responseError(response, 'Suppression refusée par le contrôle référentiel.');
    }
    if (selected.value?.boxId === box.boxId) selected.value = null;
    await loadBoxes();
  } catch (cause) {
    actionError.value = cause instanceof Error ? cause.message : 'Suppression impossible.';
  } finally {
    saving.value = false;
  }
}

function showQr(box: AdminBox): void {
  selected.value = box;
}

function printQr(): void {
  globalThis.print();
}

onMounted(loadAdminData);
</script>

<template>
  <main id="main" class="admin-shell mvpa-operational-shell ops-mobile-shell">
    <header class="admin-header mvpa-operational-header">
      <div class="mvpa-operational-brand" aria-label="OTEM Upsells Manager">
        <span aria-hidden="true">O</span>
        <strong>OTEM Upsells Manager</strong>
      </div>
      <p class="eyebrow">OTEM · Administration</p>
      <h1>
        <a class="admin-home-link" href="/admin">
          {{ props.managementMode ? 'Créer et gérer' : 'UPSELLS MANAGER' }}
        </a>
      </h1>
      <p v-if="props.managementMode">Choisissez une rubrique pour accéder à ses réglages.</p>
      <nav class="admin-primary-nav" aria-label="Navigation administration">
        <a class="button button-secondary" href="/admin/catalog">🛒Catalogue</a>
        <a class="button button-secondary" href="/admin/providers-apartments">🗂️Comptes</a>
        <a
          class="button button-secondary admin-boxes-nav"
          :class="{ 'admin-boxes-nav-damaged': damagedPhysicalBoxCount > 0 }"
          href="/admin/boxes"
        >
          <span
            v-if="damagedPhysicalBoxCount > 0"
            class="admin-boxes-nav-alert"
            data-testid="admin-boxes-nav-alert"
            :aria-label="`${damagedPhysicalBoxCount} Box Vol ou Casse à traiter`"
          >
            ⚠️
          </span>
          📦Boxs
        </a>
      </nav>
    </header>
    <nav
      v-if="props.managementMode && !managementSection"
      class="management-menu"
      aria-label="Rubriques de gestion"
    >
      <button
        v-for="item in managementSections"
        :key="item.id"
        type="button"
        :data-management="item.id"
        @click="managementSection = item.id"
      >
        <span class="management-icon" aria-hidden="true">{{ item.icon }}</span
        ><span
          ><strong>{{ item.title }}</strong
          ><small>{{ item.description }}</small></span
        ><span aria-hidden="true">›</span>
      </button>
      <a href="/admin/boxes"
        ><span class="management-icon" aria-hidden="true">□</span
        ><span><strong>Boxes et QR</strong><small>Matériel et codes d’accès</small></span
        ><span aria-hidden="true">›</span></a
      >
      <a href="/admin/inventory"
        ><span class="management-icon" aria-hidden="true">▤</span
        ><span><strong>Stock produits</strong><small>Quantités, entrées et pertes</small></span
        ><span aria-hidden="true">›</span></a
      >
    </nav>
    <div v-if="props.managementMode && managementSection" class="management-heading">
      <button
        class="button button-secondary"
        type="button"
        data-testid="management-back"
        @click="managementSection = ''"
      >
        ← Toutes les rubriques
      </button>
      <h2>{{ managementSections.find((item) => item.id === managementSection)?.title }}</h2>
    </div>
    <div v-if="props.managementMode" v-show="managementSection === 'reservations'">
      <Beds24Reservations />
    </div>

    <div
      v-if="props.managementMode"
      v-show="['team', 'apartments'].includes(managementSection)"
      class="admin-page-filter-bar admin-page-global-filter"
      data-testid="accounts-filter-bar"
      aria-label="Filtres globaux de la page Comptes"
    >
      <label for="accounts-owner-filter">
        <span>Propriétaire</span>
        <select id="accounts-owner-filter" v-model="accountsOwnerFilter">
          <option value="ALL">Tous les propriétaires</option>
          <option v-for="owner in accountsFilterOptions.owners" :key="owner" :value="owner">
            {{ owner }}
          </option>
        </select>
      </label>
      <label for="accounts-provider-filter">
        <span>Prestataire</span>
        <select id="accounts-provider-filter" v-model="accountsProviderFilter">
          <option value="ALL">Tous les prestataires</option>
          <option
            v-for="provider in accountsFilterOptions.providers"
            :key="provider"
            :value="provider"
          >
            {{ provider }}
          </option>
        </select>
      </label>
      <label for="accounts-city-filter">
        <span>Ville</span>
        <select id="accounts-city-filter" v-model="accountsCityFilter">
          <option value="ALL">Toutes les villes</option>
          <option v-for="city in accountsFilterOptions.cities" :key="city" :value="city">
            {{ city }}
          </option>
        </select>
      </label>
      <label for="accounts-operational-capacity-filter">
        <span>Capacité opérationnelle</span>
        <select
          id="accounts-operational-capacity-filter"
          v-model="accountsOperationalCapacityFilter"
        >
          <option value="ALL">Toutes les capacités</option>
          <option
            v-for="capacity in accountsFilterOptions.operationalCapacities"
            :key="capacity"
            :value="capacity"
          >
            {{ capacity }} Boxes actives
          </option>
        </select>
      </label>
    </div>

    <div
      v-if="showCatalog"
      class="admin-page-filter-bar admin-page-global-filter"
      data-testid="catalog-filter-bar"
      aria-label="Filtres globaux de la page Catalogue"
    >
      <label for="catalog-owner-filter">
        <span>Propriétaire</span>
        <select id="catalog-owner-filter" v-model="catalogOwnerFilter">
          <option value="ALL">Tous les propriétaires</option>
          <option v-for="owner in catalogFilterOptions.owners" :key="owner" :value="owner">
            {{ owner }}
          </option>
        </select>
      </label>
      <label for="catalog-provider-filter">
        <span>Prestataire</span>
        <select id="catalog-provider-filter" v-model="catalogProviderFilter">
          <option value="ALL">Tous les prestataires</option>
          <option
            v-for="provider in catalogFilterOptions.providers"
            :key="provider"
            :value="provider"
          >
            {{ provider }}
          </option>
        </select>
      </label>
      <label for="catalog-city-filter">
        <span>Ville</span>
        <select id="catalog-city-filter" v-model="catalogCityFilter">
          <option value="ALL">Toutes les villes</option>
          <option v-for="city in catalogFilterOptions.cities" :key="city" :value="city">
            {{ city }}
          </option>
        </select>
      </label>
      <label for="catalog-operational-capacity-filter">
        <span>Capacité opérationnelle</span>
        <select
          id="catalog-operational-capacity-filter"
          v-model="catalogOperationalCapacityFilter"
        >
          <option value="ALL">Toutes les capacités</option>
          <option
            v-for="capacity in catalogFilterOptions.operationalCapacities"
            :key="capacity"
            :value="capacity"
          >
            {{ capacity }} Boxes actives
          </option>
        </select>
      </label>
    </div>

    <div
      v-if="showPhysicalBoxes"
      class="admin-page-filter-bar admin-page-global-filter"
      data-testid="boxes-filter-bar"
      aria-label="Filtres globaux de la page Boxs"
    >
      <label for="boxes-owner-filter">
        <span>Propriétaire</span>
        <select id="boxes-owner-filter" v-model="boxesOwnerFilter">
          <option value="ALL">Tous les propriétaires</option>
          <option v-for="owner in boxesFilterOptions.owners" :key="owner" :value="owner">
            {{ owner }}
          </option>
        </select>
      </label>
      <label for="boxes-provider-filter">
        <span>Prestataire</span>
        <select id="boxes-provider-filter" v-model="boxesProviderFilter">
          <option value="ALL">Tous les prestataires</option>
          <option
            v-for="provider in boxesFilterOptions.providers"
            :key="provider"
            :value="provider"
          >
            {{ provider }}
          </option>
        </select>
      </label>
      <label for="boxes-city-filter">
        <span>Ville</span>
        <select id="boxes-city-filter" v-model="boxesCityFilter">
          <option value="ALL">Toutes les villes</option>
          <option v-for="city in boxesFilterOptions.cities" :key="city" :value="city">
            {{ city }}
          </option>
        </select>
      </label>
      <label for="boxes-operational-capacity-filter">
        <span>Capacité opérationnelle</span>
        <select id="boxes-operational-capacity-filter" v-model="boxesOperationalCapacityFilter">
          <option value="ALL">Toutes les capacités</option>
          <option
            v-for="capacity in boxesFilterOptions.operationalCapacities"
            :key="capacity"
            :value="capacity"
          >
            {{ capacity }} Boxes actives
          </option>
        </select>
      </label>
    </div>

    <div
      v-show="!props.managementMode || ['team', 'apartments'].includes(managementSection)"
      class="ops-toolbar"
    >
      <button
        type="button"
        class="button button-secondary"
        :disabled="
          saving ||
          (showDashboard ? dashboardLoading || deliveryLoading : boxesLoading || offersLoading)
        "
        @click="loadAdminData"
      >
        Actualiser
      </button>
    </div>
    <div v-if="props.managementMode" v-show="managementSection === 'tutorials'">
      <BoxSetupTutorial editable />
    </div>
    <section v-if="showDashboard && activeTab === 'home'" class="ops-home">
      <h2>Vue d’ensemble</h2>
      <p>Ouvrez une rubrique pour consulter les tâches à traiter.</p>
      <PaymentNotifications collapsible />
      <TaskAccordion
        v-for="group in taskGroups"
        :key="group.title"
        :title="group.title"
        :count="group.requests.length"
        :loading="deliveryLoading"
        :error="deliveryError"
      >
        <button
          v-for="request in group.requests"
          :key="request.deliveryRequestId"
          class="ops-order-row"
          type="button"
          @click="
            openOrders();
            selectedOrderId = request.deliveryRequestId;
          "
        >
          <OrderReference
            :order-id="request.deliveryRequestId"
            :created-at="request.createdAt"
          />
          <strong>{{ request.apartmentName }}</strong>
          <span class="ops-tag" :class="statusTone(request)">{{ statusLabel(request) }}</span>
          <small
            >{{ request.travelerName }} ·
            {{ request.selectedBoxOfferDisplayNameSnapshot }}</small
          >
        </button>
      </TaskAccordion>
      <TaskAccordion
        title="Stocks et boxes"
        :count="operationalTasks.length"
        :loading="operationalEstateLoading"
        :error="
          operationalEstateError ||
          (!operationalBanner && !operationalEstateLoading
            ? 'État des stocks et boxes indisponible.'
            : '')
        "
      >
        <ul v-if="operationalTasks.length">
          <li v-for="(task, index) in operationalTasks" :key="index">{{ task }}</li>
        </ul>
        <button class="button button-secondary" type="button" @click="activeTab = 'alerts'">
          Voir l’état des logements et boxes
        </button>
      </TaskAccordion>
    </section>
    <section
      v-if="showDashboard && activeTab === 'orders'"
      class="ops-orders"
      aria-label="Commandes clients"
    >
      <template v-if="selectedOrder">
        <button class="ops-back" type="button" @click="selectedOrderId = ''">
          ← Commandes
        </button>
        <h2>{{ selectedOrder.apartmentName }}</h2>
        <span class="ops-tag" :class="statusTone(selectedOrder)">{{
          statusLabel(selectedOrder)
        }}</span>
        <OrderReference
          :order-id="selectedOrder.deliveryRequestId"
          :created-at="selectedOrder.createdAt"
        />
        <h3>{{ selectedOrder.selectedBoxOfferDisplayNameSnapshot }}</h3>
        <p>
          {{ selectedOrder.travelerName }} ·
          {{ formatDeliveryDeadline(selectedOrder.deliveryDeadlineAt) }}
        </p>
        <ReservationSummary :reservation="selectedOrder.reservation" />
        <p v-if="selectedOrder.travelerComment" class="order-comment">
          <strong>Commentaire client :</strong> {{ selectedOrder.travelerComment }}
        </p>
        <TravelerCommentReview
          :order-id="selectedOrder.deliveryRequestId"
          :comment="selectedOrder.travelerComment"
          :review="selectedOrder.travelerCommentReview"
          read-only
        />
        <p v-if="selectedOrder.assignedBox">
          Box : {{ selectedOrder.assignedBox.displayName }}
        </p>
        <BoxWithdrawal
          v-for="box in boxes.filter(
            (b) => b.withdrawalRequestId === selectedOrder?.deliveryRequestId,
          )"
          :key="box.boxId"
          :box-id="box.boxId"
          :request-id="box.withdrawalRequestId"
          role="admin"
          :disabled="saving || deliveryLoading"
          @changed="loadAdminData"
        />
        <p>
          Préparation confirmée par : {{ selectedOrder.preparedByProviderId ?? 'En attente' }}
        </p>
        <article
          v-if="!selectedOrder.funding"
          class="ops-next"
          data-testid="order-payment-detail"
        >
          <template v-if="selectedOrder.prepaymentStatus === 'PAID' && selectedOrder.payment">
            <strong>Prépaiement confirmé · {{ formatPrice(selectedOrder.payment) }}</strong>
            <p>Référence {{ selectedOrder.payment.paymentAttemptId }}</p>
            <p>{{ new Date(selectedOrder.payment.paidAt).toLocaleString('fr-FR') }}</p>
          </template>
          <p v-else>
            {{
              selectedOrder.prepaymentStatus === 'NOT_PREPAID'
                ? 'Aucun prépaiement associé à cette demande.'
                : 'Information de prépaiement indisponible.'
            }}
          </p>
        </article>
        <p
          v-if="
            !selectedOrder.funding &&
            selectedOrder.modifications?.some((m) => m.status === 'APPLIED')
          "
        >
          Montant réglé après ajustements, avant éventuelle annulation :
          {{
            formatPrice({
              amountMinor: selectedOrder.selectedBoxOfferAmountMinorSnapshot,
              currency: 'EUR',
            })
          }}
        </p>
        <OrderManagement
          :key="selectedOrder.deliveryRequestId"
          role="admin"
          :order="selectedOrder"
          :disabled="deliveryLoading || Boolean(deliveryError)"
          @changed="loadAdminData"
        />
        <p v-if="selectedOrder.providerException" class="ops-alert">
          {{
            selectedOrder.providerException.refusalReason ??
            selectedOrder.providerException.proposedAlternative
          }}
          · Arbitrage requis. Aucun remboursement automatique.
        </p>
      </template>
      <template v-else>
        <h2>Commandes</h2>
        <div class="ops-filters">
          <button
            type="button"
            :aria-pressed="orderFilter === 'ALL'"
            @click="orderFilter = 'ALL'"
          >
            Toutes</button
          ><button
            type="button"
            :aria-pressed="orderFilter === 'REMAINING'"
            @click="orderFilter = 'REMAINING'"
          >
            À livrer</button
          ><button
            type="button"
            :aria-pressed="orderFilter === 'ALERTS'"
            @click="orderFilter = 'ALERTS'"
          >
            Alertes
          </button>
        </div>
        <p v-if="deliveryLoading" role="status">Chargement des commandes…</p>
        <p v-else-if="deliveryError" role="alert">{{ deliveryError }}</p>
        <button
          v-for="request in visibleOrders"
          :key="request.deliveryRequestId"
          class="ops-order-row"
          type="button"
          :data-testid="`ops-order-${request.deliveryRequestId}`"
          @click="selectedOrderId = request.deliveryRequestId"
        >
          <small v-if="request.modifications?.length" class="order-change-outcome"
            >Dernier changement : {{ modificationLabel(request.modifications.at(-1)!) }}</small
          >
          <OrderReference
            :order-id="request.deliveryRequestId"
            :created-at="request.createdAt"
          />
          <strong>{{ request.apartmentName }}</strong
          ><span class="ops-tag" :class="statusTone(request)">{{ statusLabel(request) }}</span
          ><small
            >{{ request.travelerName
            }}<template v-if="request.assignedBox">
              · {{ request.assignedBox.displayName }}</template
            ></small
          >
          <small
            >{{ request.selectedBoxOfferDisplayNameSnapshot }} ·
            {{
              request.adminRefusal?.status === 'REFUSED'
                ? 'Aucun prélèvement'
                : request.adminRefusal
                  ? 'Libération bancaire à vérifier'
                  : request.prepaymentStatus === 'AUTHORIZED'
                    ? 'Montant réservé · prélèvement à la livraison'
                    : request.prepaymentStatus === 'PAID'
                      ? 'Prépaiement confirmé'
                      : request.prepaymentStatus === 'NOT_PREPAID'
                        ? 'Sans prépaiement associé'
                        : 'Prépaiement à vérifier'
            }}</small
          >
        </button>
        <p v-if="!deliveryLoading && !deliveryError && !visibleOrders.length">
          Aucune commande dans ce périmètre.
        </p>
      </template>
    </section>
    <section
      v-if="showDashboard"
      v-show="activeTab === 'stats'"
      class="admin-upsell-dashboard"
      aria-labelledby="upsell-dashboard-title"
    >
      <div class="admin-section-header">
        <div class="admin-dashboard-title">
          <p class="admin-section-kicker">Ventes validées</p>
          <h2 id="upsell-dashboard-title">Dashboard upsells</h2>
        </div>
      </div>
      <p v-if="dashboardLoading" class="flow-status">Calcul des indicateurs…</p>
      <p v-else-if="dashboardError" class="flow-error">{{ dashboardError }}</p>
      <div
        v-if="!dashboardLoading && !dashboardError && selectedUpsellMetrics"
        class="ops-kpis"
        data-testid="ops-kpis"
      >
        <button type="button" @click="showPaidSales = !showPaidSales">
          <span>Montant payé</span
          ><strong>{{
            formatPrice({
              amountMinor: selectedUpsellMetrics.upsellRevenueMinor,
              currency: 'EUR',
            })
          }}</strong
          ><small>Paiements confirmés ›</small>
        </button>
        <button type="button" @click="showPaidSales = !showPaidSales">
          <span>Ventes payées</span><strong>{{ selectedUpsellMetrics.upsellSalesCount }}</strong
          ><small>Voir les paiements ›</small>
        </button>
        <button type="button" @click="showPaidSales = !showPaidSales">
          <span>Panier moyen</span
          ><strong>{{
            selectedUpsellMetrics.upsellSalesCount
              ? formatPrice({
                  amountMinor: selectedUpsellMetrics.averageUpsellBasketMinor,
                  currency: 'EUR',
                })
              : '—'
          }}</strong
          ><small>Par vente payée ›</small>
        </button>
        <button
          type="button"
          :disabled="deliveryLoading || !!deliveryError"
          @click="openOrders('REMAINING')"
        >
          <span>À livrer</span
          ><strong>{{ deliveryLoading || deliveryError ? '—' : remainingOrders.length }}</strong
          ><small>Toutes les commandes ›</small>
        </button>
      </div>
      <p class="ops-kpi-note">
        Montants nets connus après compléments et remboursements, hors frais. Les opérations en
        attente figurent dans le détail des commandes. « À livrer » couvre toutes les commandes
        en attente.
      </p>
      <p
        v-if="!dashboardLoading && !dashboardError && dashboardScope.startsWith('OWNER:')"
        class="ops-kpi-note"
      >
        Pour les changements d’offre, choisissez « Tous » ou un appartement : le rattachement
        par propriétaire n’est pas disponible.
      </p>
      <OfferChangeStats
        v-if="
          !dashboardLoading &&
          !dashboardError &&
          upsellDashboard?.offerChangesByScope?.[dashboardScope]
        "
        :summary="upsellDashboard.offerChangesByScope[dashboardScope]!"
      />
      <div
        v-if="showPaidSales && !dashboardLoading && !dashboardError"
        data-testid="ops-paid-sales"
      >
        <h3>Paiements du périmètre</h3>
        <article
          v-for="sale in selectedSales"
          :key="sale.paymentAttemptId"
          class="ops-order-row"
        >
          <strong>{{ formatPrice({ amountMinor: sale.amountMinor, currency: 'EUR' }) }}</strong>
          <p>{{ sale.apartmentName ?? 'Sans appartement associé' }}</p>
          <small
            >{{ sale.paymentAttemptId }} ·
            {{ new Date(sale.paidAt).toLocaleString('fr-FR') }}</small
          >
          <button
            v-if="
              sale.deliveryRequestId &&
              deliveryRequests.some(
                (request) => request.deliveryRequestId === sale.deliveryRequestId,
              )
            "
            class="ops-back"
            type="button"
            @click="
              openOrders();
              selectedOrderId = sale.deliveryRequestId!;
            "
          >
            Ouvrir la commande →
          </button>
          <p v-else>
            {{
              sale.deliveryRequestId
                ? 'Commande indisponible : actualisez les données.'
                : 'Achat sur place, sans demande de livraison associée.'
            }}
          </p>
        </article>
        <p v-if="!selectedSales.length">
          {{
            upsellDashboard?.salesByScope
              ? 'Aucun paiement dans ce périmètre.'
              : 'Détail des paiements indisponible sur cette version du serveur.'
          }}
        </p>
      </div>
      <div
        v-if="!dashboardLoading && !dashboardError && selectedUpsellMetrics"
        class="admin-pie-dashboard"
      >
        <figure class="admin-pie-figure" data-testid="upsell-pie-chart">
          <div
            class="admin-pie-chart"
            :style="dashboardPieStyle"
            role="img"
            :aria-label="`Répartition dynamique du chiffre d’affaires, ${dashboardPieSlices.length} périmètre(s)`"
          >
            <span>
              <strong data-testid="upsell-revenue-kpi">{{
                formatPrice({
                  amountMinor: selectedUpsellMetrics.upsellRevenueMinor,
                  currency: 'EUR',
                })
              }}</strong>
              <small>CA upsells</small>
            </span>
          </div>
          <figcaption>{{ dashboardPieCaption }}</figcaption>
        </figure>
        <ul class="admin-pie-legend" aria-label="Détail du graphique">
          <li v-if="dashboardPieSlices.length === 0">Aucune vente dans ce périmètre.</li>
          <li v-for="slice in dashboardPieSlices" :key="slice.label">
            <i :style="{ background: slice.color }" aria-hidden="true"></i>
            <span>{{ slice.label }}</span>
            <strong>{{
              formatPrice({ amountMinor: slice.valueMinor, currency: 'EUR' })
            }}</strong>
          </li>
        </ul>
        <dl class="admin-kpi-summary">
          <div>
            <dt>Ventes</dt>
            <dd data-testid="upsell-sales-count-kpi">
              {{ selectedUpsellMetrics.upsellSalesCount }}
            </dd>
          </div>
          <div>
            <dt>Panier moyen</dt>
            <dd data-testid="average-upsell-basket-kpi">
              {{
                formatPrice({
                  amountMinor: selectedUpsellMetrics.averageUpsellBasketMinor,
                  currency: 'EUR',
                })
              }}
            </dd>
          </div>
          <div>
            <dt>Marge brute estimée</dt>
            <dd data-testid="estimated-gross-margin-kpi">
              <template v-if="selectedUpsellMetrics.estimatedGrossMarginMinor !== null">
                {{
                  formatPrice({
                    amountMinor: selectedUpsellMetrics.estimatedGrossMarginMinor,
                    currency: 'EUR',
                  })
                }}
              </template>
              <template v-else>INCONNU</template>
            </dd>
          </div>
        </dl>
      </div>
      <AnnualPerformance v-if="activeTab === 'stats'" />
      <div class="admin-dashboard-filter-bar" data-testid="admin-dashboard-filter-bar">
        <label class="admin-dashboard-filter" for="upsell-dashboard-scope">
          <span>Périmètre</span>
          <select
            id="upsell-dashboard-scope"
            v-model="dashboardScope"
            data-testid="upsell-dashboard-filter"
          >
            <option
              v-for="filter in upsellDashboard?.filters ?? []"
              :key="filter.key"
              :value="filter.key"
            >
              {{ filter.label }}
            </option>
          </select>
        </label>
        <label class="admin-dashboard-filter" for="upsell-dashboard-period">
          <span>Période</span>
          <select
            id="upsell-dashboard-period"
            v-model="dashboardPeriod"
            data-testid="upsell-dashboard-period"
            @change="loadUpsellDashboard"
          >
            <option value="CURRENT_MONTH">Mois en cours</option>
            <option value="CURRENT_YEAR">Année en cours</option>
            <option value="ALL">Vue globale</option>
          </select>
        </label>
        <label class="admin-dashboard-filter" for="upsell-dashboard-offer">
          <span>Offre</span>
          <select
            id="upsell-dashboard-offer"
            v-model="dashboardOfferId"
            data-testid="upsell-dashboard-offer-filter"
            @change="loadUpsellDashboard"
          >
            <option
              v-for="filter in upsellDashboard?.offerFilters ?? [
                { key: 'ALL', label: 'Toutes les offres' },
              ]"
              :key="filter.key"
              :value="filter.key"
            >
              {{ filter.label }}
            </option>
          </select>
        </label>
        <label class="admin-dashboard-filter" for="upsell-dashboard-sale-mode">
          <span>Statut vente</span>
          <select
            id="upsell-dashboard-sale-mode"
            v-model="dashboardSaleMode"
            data-testid="upsell-dashboard-sale-mode-filter"
            @change="loadUpsellDashboard"
          >
            <option value="ALL">Tous les statuts de vente</option>
            <option value="ORDERED">Commandé</option>
            <option value="SELF_SERVICE">Libre service</option>
          </select>
        </label>
      </div>
    </section>

    <section
      v-if="props.managementMode"
      v-show="managementSection === 'team'"
      class="admin-provider-accounts"
      aria-labelledby="provider-accounts-title"
    >
      <div class="admin-section-header">
        <div>
          <p class="admin-section-kicker">Accès opérationnels</p>
          <h2 id="provider-accounts-title">Comptes prestataires</h2>
        </div>
        <div class="admin-actions">
          <span class="admin-count-badge">{{ filteredProviderAccounts.length }}</span>
          <button
            class="button button-primary"
            type="button"
            data-testid="open-provider-create"
            @click="beginProviderCreate"
          >
            Créer un prestataire
          </button>
        </div>
      </div>
      <p v-if="providerAccountsLoading" class="flow-status">Chargement des comptes…</p>
      <div v-else-if="filteredProviderAccounts.length" class="provider-account-grid">
        <article
          v-for="account in filteredProviderAccounts"
          :key="account.providerId"
          class="provider-account-card"
          :data-testid="`provider-account-${account.providerId}`"
        >
          <strong>{{ account.providerId }}</strong>
          <span>{{ account.username }}</span>
          <span>{{ account.active ? 'Actif' : 'Désactivé' }}</span>
          <small>Villes : {{ account.scopes.cities.join(', ') || 'Toutes connues' }}</small>
          <small>
            Appartements : {{ account.scopes.apartments.join(', ') || 'Tous connus' }}
          </small>
          <small>
            Propriétaires : {{ account.scopes.owners.join(', ') || 'Tous connus' }}
          </small>
          <div class="admin-actions">
            <button
              class="button button-secondary"
              type="button"
              @click="beginProviderEdit(account)"
            >
              Modifier
            </button>
            <button
              v-if="account.active"
              class="button button-danger"
              type="button"
              @click="disableProviderAccount(account)"
            >
              Supprimer
            </button>
          </div>
        </article>
      </div>
      <p v-else class="flow-status" data-testid="accounts-filter-empty">Aucun résultat.</p>
      <form
        v-if="providerFormOpen"
        class="provider-account-form"
        data-testid="provider-account-form"
        @submit.prevent="saveProviderAccount"
      >
        <h3>{{ editingProviderId ? 'Modifier le prestataire' : 'Créer un prestataire' }}</h3>
        <p class="flow-status">
          Sélectionnez les villes ou appartements confiés à ce préparateur.
        </p>
        <label for="provider-id">Identifiant prestataire</label>
        <input
          id="provider-id"
          v-model="providerDraft.providerId"
          :disabled="Boolean(editingProviderId)"
          required
        />
        <label for="provider-username">Nom d’utilisateur</label>
        <input
          id="provider-username"
          v-model="providerDraft.username"
          autocomplete="off"
          required
        />
        <label for="provider-password">
          {{ editingProviderId ? 'Nouveau mot de passe (facultatif)' : 'Mot de passe' }}
        </label>
        <input
          id="provider-password"
          v-model="providerDraft.password"
          type="password"
          autocomplete="new-password"
        />
        <fieldset
          v-for="dimension in providerScopeDimensions"
          :key="dimension.key"
          class="provider-scope-fieldset"
        >
          <legend>{{ dimension.label }}</legend>
          <p v-if="providerScopeOptions[dimension.key].length === 0" class="flow-status">
            Aucune valeur connue — aucune valeur inventée.
          </p>
          <label v-for="option in providerScopeOptions[dimension.key]" :key="option">
            <input
              v-model="providerDraft.scopes[dimension.key]"
              type="checkbox"
              :value="option"
            />
            <span>{{ option }}</span>
          </label>
        </fieldset>
        <p v-if="providerScopeError" class="flow-error" role="alert">
          {{ providerScopeError }}
        </p>
        <div class="admin-actions">
          <button class="button button-primary" type="submit" :disabled="saving">
            {{ editingProviderId ? 'Enregistrer le compte' : 'Créer le compte' }}
          </button>
          <button class="button button-secondary" type="button" @click="resetProviderDraft">
            Annuler
          </button>
        </div>
      </form>
      <section
        v-if="providerFormOpen"
        class="provider-scope-manager"
        aria-labelledby="provider-scope-manager-title"
      >
        <h3 id="provider-scope-manager-title">Villes, appartements et propriétaires</h3>
        <div
          v-for="dimension in providerScopeDimensions"
          :key="`manager-${dimension.key}`"
          class="provider-scope-list"
          :data-testid="`provider-scope-list-${dimension.key}`"
        >
          <strong>{{ dimension.label }}</strong>
          <ul>
            <li v-for="option in providerScopeOptions[dimension.key]" :key="option">
              {{ option }}
            </li>
          </ul>
          <div class="provider-scope-create-row">
            <input
              v-model="providerScopeDrafts[dimension.key][0]"
              :aria-label="`Créer · ${dimension.label}`"
              maxlength="128"
              :placeholder="`Ajouter · ${dimension.label.toLowerCase()}`"
            />
            <button
              class="button button-secondary"
              type="button"
              :disabled="saving || !providerScopeDrafts[dimension.key][0]?.trim()"
              @click="addProviderScopeValue(dimension.key)"
            >
              Ajouter
            </button>
          </div>
        </div>
      </section>
    </section>

    <section
      v-if="props.managementMode"
      v-show="managementSection === 'apartments'"
      class="admin-provider-accounts apartment-configuration-panel"
      aria-labelledby="apartment-configurations-title"
    >
      <div class="admin-section-header">
        <div>
          <p class="admin-section-kicker">Capacité opérationnelle</p>
          <h2 id="apartment-configurations-title">Offres &amp; Boxes par appartement</h2>
        </div>
        <div class="admin-actions">
          <span class="admin-count-badge">{{ filteredApartmentConfigurations.length }}</span>
          <button
            class="button button-primary"
            type="button"
            data-testid="open-apartment-configuration"
            @click="beginApartmentConfiguration()"
          >
            Créer un appartement
          </button>
        </div>
      </div>
      <div v-if="filteredApartmentConfigurations.length" class="apartment-configuration-grid">
        <article
          v-for="configuration in filteredApartmentConfigurations"
          :key="configuration.apartment"
          class="provider-account-card"
          :data-testid="`apartment-configuration-${configuration.apartment}`"
        >
          <strong>{{ configuration.apartment }}</strong>
          <span>Ville · {{ configuration.city ?? 'Non renseignée' }}</span>
          <span>Propriétaire · {{ configuration.owner }}</span>
          <span>N° rue · {{ configuration.streetNumber ?? 'Non renseigné' }}</span>
          <span>
            Boxes actives · {{ configuration.activeBoxCount }}/{{
              configuration.activeBoxTarget
            }}
          </span>
          <small>
            Catalogue voyageur proposé ·
            {{ configuration.offers.map((offer) => offer.displayName).join(', ') }}
          </small>
          <small>
            Maximum de Boxes actives dans le logement · {{ configuration.activeBoxTarget }}
          </small>
          <div class="admin-actions">
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`edit-apartment-configuration-${configuration.apartment}`"
              @click="beginApartmentConfiguration(configuration)"
            >
              Modifier
            </button>
            <button
              class="button button-danger"
              type="button"
              :data-testid="`delete-apartment-configuration-${configuration.apartment}`"
              @click="deleteApartmentConfiguration(configuration)"
            >
              Supprimer
            </button>
          </div>
        </article>
      </div>
      <p v-else class="flow-status" data-testid="apartments-filter-empty">Aucun résultat.</p>
      <form
        v-if="apartmentConfigurationFormOpen"
        class="provider-account-form"
        data-testid="apartment-configuration-form"
        @submit.prevent="saveApartmentConfiguration"
      >
        <h3>Créer un appartement</h3>
        <label for="apartment-configuration-apartment">Nom appartement</label>
        <input
          id="apartment-configuration-apartment"
          v-model="apartmentConfigurationDraft.apartment"
          required
        />
        <label for="apartment-configuration-city">Ville</label>
        <input
          id="apartment-configuration-city"
          v-model="apartmentConfigurationDraft.city"
          required
        />
        <label for="apartment-configuration-owner">Nom propriétaire</label>
        <input
          id="apartment-configuration-owner"
          v-model="apartmentConfigurationDraft.owner"
          required
        />
        <label for="apartment-configuration-street-number">Numéro rue</label>
        <input
          id="apartment-configuration-street-number"
          v-model="apartmentConfigurationDraft.streetNumber"
          required
        />
        <fieldset class="provider-scope-fieldset">
          <legend>Offres affectées · catalogue proposé aux voyageurs</legend>
          <label v-for="offer in boxOffers" :key="offer.offerId">
            <input
              v-model="apartmentConfigurationDraft.offerIds"
              type="checkbox"
              :value="offer.offerId"
            />
            <span>{{ offer.displayName }}</span>
          </label>
        </fieldset>
        <label for="apartment-active-box-target">
          Nombre maximum de Boxes actives dans le logement
        </label>
        <input
          id="apartment-active-box-target"
          v-model.number="apartmentConfigurationDraft.activeBoxTarget"
          type="number"
          min="1"
          max="20"
          required
        />
        <small>
          Ce maximum ne limite pas le stock de Boxes disponibles ni les remplacements en
          attente.
        </small>
        <div class="admin-actions">
          <button class="button button-primary" type="submit" :disabled="saving">
            Enregistrer la configuration
          </button>
          <button
            class="button button-secondary"
            type="button"
            @click="cancelApartmentConfiguration"
          >
            Annuler
          </button>
        </div>
      </form>
    </section>

    <p v-if="actionError" class="flow-error" role="alert">{{ actionError }}</p>
    <div v-if="actionSuccess" class="admin-action-success" role="status">
      <strong>{{ actionSuccess }}</strong>
    </div>

    <section
      v-if="showDashboard && operationalBanner"
      v-show="activeTab === 'alerts'"
      class="admin-operational-banner"
      aria-labelledby="admin-operational-banner-title"
      data-testid="admin-operational-banner"
    >
      <h2 id="admin-operational-banner-title">Situation opérationnelle</h2>
      <p>
        <strong class="admin-operational-banner-title">Consommables :</strong>
        <span
          :class="{
            'admin-operational-banner-problem': operationalConsumableNames.length > 0,
          }"
        >
          {{
            operationalConsumableNames.length
              ? `Réappro ${operationalConsumableNames.join(' et ')}`
              : '✅'
          }}
        </span>
      </p>
      <p>
        <strong class="admin-operational-banner-title">Boxs :</strong>
        <span
          :class="{
            'admin-operational-banner-problem': operationalBanner.missingBoxCount > 0,
          }"
        >
          {{
            operationalBanner.missingBoxCount > 0
              ? `${operationalBanner.missingBoxCount} manquante${operationalBanner.missingBoxCount > 1 ? 's' : ''}, blocage vente`
              : '✅'
          }}
        </span>
      </p>
      <p>
        <strong class="admin-operational-banner-title">Recup Boxs :</strong>
        <span :class="{ 'admin-operational-banner-problem': operationalRecoveryProblem }">
          {{
            !operationalRecoveryProblem
              ? '✅'
              : `${operationalBanner.damageCount} casse · ${operationalBanner.theftCount} vol`
          }}
          <template v-if="operationalBanner.pendingRecoveryCount > 0">
            · {{ operationalBanner.pendingRecoveryCount }} récupération(s) en attente
          </template>
        </span>
      </p>
      <p v-if="operationalBanner.noEligibleAlerts.length" class="flow-notice-warning">
        🆘 {{ operationalBanner.noEligibleAlerts.length }} alerte(s) Prestataire sans Box
        éligible.
      </p>
    </section>

    <section
      v-if="showDashboard"
      v-show="activeTab === 'alerts'"
      class="admin-delivery-queue"
      aria-labelledby="delivery-queue-title"
    >
      <div class="admin-section-header">
        <div>
          <p class="admin-section-kicker">Tableau de statut visuel</p>
          <h2 id="delivery-queue-title">Statuts Appartements/Box</h2>
        </div>
        <span v-if="activeDeliveryRequests.length" class="admin-count-badge">
          {{ activeDeliveryRequests.length }} en cours
        </span>
      </div>
      <fieldset class="admin-operational-filters" data-testid="admin-operational-filters">
        <legend>Filtres opérationnels du tableau de statut</legend>
        <label class="admin-operational-filter" for="admin-operational-status">
          <span>Statut Box</span>
          <select id="admin-operational-status" v-model="operationalStatusFilter">
            <option value="ALL">Tous les statuts</option>
            <option v-for="tone in operationalStatusOptions" :key="tone" :value="tone">
              {{
                tone === 'to-prepare'
                  ? 'À préparer'
                  : tone === 'consumed'
                    ? 'Vendue'
                    : tone === 'installed'
                      ? 'Prête'
                      : 'Commande'
              }}
            </option>
          </select>
        </label>
        <label class="admin-operational-filter" for="admin-operational-provider">
          <span>Prestataire</span>
          <select id="admin-operational-provider" v-model="operationalProviderFilter">
            <option value="ALL">Tous les prestataires</option>
            <option value="UNASSIGNED">Non affecté</option>
            <option
              v-for="provider in operationalProviderOptions"
              :key="provider"
              :value="provider"
            >
              {{ provider }}
            </option>
          </select>
        </label>
        <label class="admin-operational-filter" for="admin-operational-client">
          <span>Client</span>
          <select id="admin-operational-client" v-model="operationalClientFilter">
            <option value="ALL">Tous les clients</option>
            <option v-for="client in operationalClientOptions" :key="client" :value="client">
              {{ client }}
            </option>
          </select>
        </label>
        <label class="admin-operational-filter" for="admin-operational-city">
          <span>Ville</span>
          <select id="admin-operational-city" v-model="operationalCityFilter">
            <option value="ALL">Toutes les villes</option>
            <option v-for="city in operationalCityOptions" :key="city" :value="city">
              {{ city }}
            </option>
          </select>
        </label>
        <label class="admin-operational-filter" for="admin-operational-apartment">
          <span>Appartement</span>
          <select id="admin-operational-apartment" v-model="operationalApartmentFilter">
            <option value="ALL">Tous les appartements</option>
            <option
              v-for="apartment in operationalApartmentOptions"
              :key="apartment"
              :value="apartment"
            >
              {{ apartment }}
            </option>
          </select>
        </label>
      </fieldset>
      <div class="admin-cleaning-badges" data-testid="admin-cleaning-priority-badges">
        <article
          v-for="badge in cleaningPriorityBadges"
          :key="badge.key"
          class="admin-cleaning-badge"
          :class="`admin-cleaning-badge-${badge.tone}`"
          :data-box-status="badge.tone"
        >
          <strong>{{ badge.apartmentLabel }}</strong>
          <small
            >{{ badge.city ?? 'Ville non renseignée' }} · Client
            {{ badge.client ?? 'non renseigné' }}</small
          >
          <small>
            Prestataire{{ badge.providerIds.length > 1 ? 's' : '' }} ·
            {{ badge.providerIds.join(', ') || 'non affecté' }}
          </small>
          <span v-if="badge.offer" class="admin-cleaning-badge-offer">{{ badge.offer }}</span>
          <span class="admin-cleaning-badge-coefficient">
            Coef × {{ badge.minimumSaleCoefficient.toLocaleString('fr-FR') }}
          </span>
          <span>{{ badge.label }}</span>
          <button
            v-if="badge.alert"
            class="admin-cleaning-badge-alert"
            type="button"
            :aria-expanded="expandedAlertKey === badge.key"
            @click="expandedAlertKey = expandedAlertKey === badge.key ? '' : badge.key"
          >
            ⚠ ALERTE
          </button>
          <div
            v-if="badge.alert && expandedAlertKey === badge.key"
            class="admin-cleaning-badge-alert-details"
            :data-testid="`admin-alert-details-${badge.key}`"
          >
            <ul v-if="badge.alertDetails.length">
              <li
                v-for="detail in badge.alertDetails"
                :key="`${detail.missingProductName}-${detail.providerId}`"
              >
                {{ detail.missingProductName }} · attendu {{ detail.missingQuantity }} · =&gt;
                {{ detail.replacementProductName ?? detail.missingProductName }} · préparé
                {{ detail.actualQuantity }} · prestataire {{ detail.providerId }}
              </li>
            </ul>
            <p
              v-if="badge.offerChangeAlert"
              class="admin-provider-offer-change-alert"
              data-testid="admin-provider-offer-change-alert"
            >
              Offre changée par {{ badge.offerChangeAlert.providerId }} ·
              {{ badge.offerChangeAlert.previousOfferDisplayName }} →
              {{ badge.offerChangeAlert.newOfferDisplayName }}
            </p>
          </div>
        </article>
      </div>
      <div
        v-if="exceptionDeliveryRequests.length"
        class="provider-preparation-list"
        aria-label="Exceptions de livraison transmises au gestionnaire"
        data-testid="admin-delivery-exceptions"
      >
        <article
          v-for="request in exceptionDeliveryRequests"
          :key="request.deliveryRequestId"
          class="admin-delivery-card admin-delivery-exception-card"
          :data-testid="`admin-delivery-exception-${request.deliveryRequestId}`"
        >
          <p class="admin-delivery-phase">
            {{
              request.deliveryLifecycleState === 'MODIFICATION_PROPOSEE'
                ? 'Modification proposée'
                : 'Demande refusée'
            }}
          </p>
          <h3>{{ request.apartmentName }}</h3>
          <p><strong>Demande :</strong> {{ request.deliveryRequestId }}</p>
          <p>
            <strong>Échéance :</strong>
            <time :datetime="request.deliveryDeadlineAt">
              {{ formatDeliveryDeadline(request.deliveryDeadlineAt) }}
            </time>
          </p>
          <p>
            <strong>Prestataire :</strong>
            {{ request.providerException?.providerId }}
          </p>
          <p v-if="request.providerException?.proposedAlternative">
            <strong>Alternative proposée :</strong>
            {{ request.providerException.proposedAlternative }}
          </p>
          <p v-if="request.providerException?.refusalReason">
            <strong>Motif du refus :</strong>
            {{ request.providerException.refusalReason }}
          </p>
          <p v-if="request.absentEntryAuthorized">
            <strong>Entrée en l’absence du voyageur :</strong> autorisée
          </p>
          <p class="ops-alert" role="status">
            Commentaire préparateur — action administrateur requise.
          </p>
          <button
            class="button button-primary"
            type="button"
            @click="
              openOrders();
              selectedOrderId = request.deliveryRequestId;
            "
          >
            Consulter la commande
          </button>
        </article>
      </div>
      <p v-if="deliveryLoading" class="flow-status" role="status">Chargement des commandes…</p>
      <p v-else-if="operationalEstateLoading" class="flow-status" role="status">
        Chargement du parc global…
      </p>
      <p v-else-if="operationalEstateError" class="flow-error" role="alert">
        {{ operationalEstateError }}
      </p>
      <p v-else-if="deliveryError" class="flow-error" role="alert">{{ deliveryError }}</p>
      <p v-else-if="cleaningPriorityBadges.length === 0" class="flow-status">
        Aucun statut correspondant aux filtres.
      </p>
    </section>

    <section v-if="showCatalog" class="admin-offers-section" aria-labelledby="box-offers-title">
      <div class="admin-section-header">
        <div>
          <p class="admin-section-kicker">Catalogue voyageur</p>
          <h2 id="box-offers-title">Offres de Box</h2>
        </div>
        <span v-if="boxOffers.length" class="admin-box-count">{{
          filteredBoxOffers.length
        }}</span>
      </div>
      <details class="admin-create-panel">
        <summary>Créer une offre</summary>
        <form
          class="admin-create-form"
          data-testid="create-box-offer-form"
          @submit.prevent="createOffer"
        >
          <label for="new-offer-name">Nom de l’offre</label>
          <input id="new-offer-name" v-model="newOfferDisplayName" maxlength="128" required />
          <label for="new-offer-price">Prix EUR</label>
          <input
            id="new-offer-price"
            v-model="newOfferAmountEuros"
            inputmode="decimal"
            required
          />
          <button class="button button-primary" type="submit" :disabled="saving">
            Créer l’offre
          </button>
        </form>
      </details>
      <p v-if="offersLoading" class="flow-status" role="status">Chargement des offres…</p>
      <p v-else-if="offersError" class="flow-error" role="alert">{{ offersError }}</p>
      <div
        v-if="filteredBoxOffers.length"
        class="admin-offer-list"
        aria-label="Liste des offres de Box"
      >
        <OfferGallery
          v-if="!filteredBoxOffers.some((o) => o.offerId === selectedOfferId)"
          :offers="filteredBoxOffers"
          :orders="deliveryRequests"
          :statuses-available="!deliveryLoading && !deliveryError"
          @select="selectedOfferId = $event"
        />
        <article
          v-for="offer in filteredBoxOffers"
          v-show="offer.offerId === selectedOfferId"
          :key="offer.offerId"
          class="admin-offer-card"
        >
          <button class="button button-secondary" type="button" @click="selectedOfferId = ''">
            ← Toutes les offres
          </button>
          <figure class="admin-offer-photo" :data-testid="`admin-offer-photo-${offer.offerId}`">
            <img
              v-if="offer.photoDataUrl"
              :src="offer.photoDataUrl"
              :alt="`Photo de l’offre ${offer.displayName}`"
            />
            <div v-else class="admin-offer-photo-placeholder" aria-hidden="true">
              <span>Photo</span>
              <small>Photo (Format Paysage) — JPEG, PNG ou WebP</small>
            </div>
            <figcaption>
              <label
                class="button button-secondary admin-offer-photo-upload"
                :for="`offer-photo-${offer.offerId}`"
              >
                {{ offer.photoDataUrl ? 'Remplacer la photo' : 'Ajouter une photo' }}
              </label>
              <input
                :id="`offer-photo-${offer.offerId}`"
                class="admin-offer-photo-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                :disabled="saving"
                :data-testid="`offer-photo-upload-${offer.offerId}`"
                @change="uploadOfferPhoto(offer, $event)"
              />
              <button
                v-if="offer.photoDataUrl"
                class="button button-secondary"
                type="button"
                :disabled="saving"
                :data-testid="`offer-photo-remove-${offer.offerId}`"
                @click="persistOfferPhoto(offer, null)"
              >
                Retirer
              </button>
            </figcaption>
          </figure>
          <div>
            <h3>{{ offer.displayName }}</h3>
            <p class="admin-price">Prix de vente réel : {{ formatPrice(offer) }}</p>
            <p>
              Coût d’achat total :
              <strong>
                {{
                  formatPrice({
                    amountMinor: offer.totalPurchasePriceMinor ?? 0,
                    currency: 'EUR',
                  })
                }}
              </strong>
            </p>
            <p class="admin-margin-estimate" data-testid="minimum-sale-price-policy">
              Prix de vente mini :
              <strong>
                {{
                  formatPrice({
                    amountMinor: offer.minimumSalePriceMinor ?? 0,
                    currency: 'EUR',
                  })
                }}
              </strong>
              <small>
                Coef ×
                {{ (offer.minimumSaleCoefficient ?? 2).toLocaleString('fr-FR') }}
              </small>
            </p>
            <ul v-if="(offer.products ?? []).length" class="admin-product-list">
              <li v-for="product in offer.products" :key="product.name">
                <strong>{{ product.name }}</strong>
                <span>Qté {{ product.quantity }}</span>
                <span>
                  Prix d’achat unitaire
                  {{
                    formatPrice({ amountMinor: product.purchasePriceMinor, currency: 'EUR' })
                  }}
                </span>
              </li>
            </ul>
            <p v-else class="flow-status">Composition produit à renseigner.</p>
          </div>
          <form
            v-if="editingOfferCoefficientId === offer.offerId"
            @submit.prevent="saveMinimumCoefficient(offer)"
          >
            <label :for="`offer-coefficient-${offer.offerId}`">Coefficient minimum</label>
            <input
              :id="`offer-coefficient-${offer.offerId}`"
              v-model="offerCoefficient"
              inputmode="decimal"
              required
            />
            <button class="button button-primary" type="submit" :disabled="saving">
              Enregistrer le coefficient
            </button>
          </form>
          <form
            v-if="editingOfferPriceId === offer.offerId"
            @submit.prevent="saveOffer(offer, 'price')"
          >
            <label :for="`offer-price-${offer.offerId}`">Prix de vente réel EUR</label>
            <input
              :id="`offer-price-${offer.offerId}`"
              v-model="offerAmountEuros"
              inputmode="decimal"
              required
            />
            <button class="button button-primary" type="submit" :disabled="saving">
              Enregistrer
            </button>
          </form>
          <form
            v-if="editingOfferNameId === offer.offerId"
            @submit.prevent="saveOffer(offer, 'display-name')"
          >
            <label :for="`offer-name-${offer.offerId}`">Nom de l’offre</label>
            <input
              :id="`offer-name-${offer.offerId}`"
              v-model="offerDisplayName"
              maxlength="128"
              required
            />
            <button class="button button-primary" type="submit" :disabled="saving">
              Renommer
            </button>
          </form>
          <form
            v-if="editingOfferProductsId === offer.offerId"
            class="admin-products-editor"
            :data-testid="`offer-products-editor-${offer.offerId}`"
            @submit.prevent="saveOfferProducts(offer)"
          >
            <fieldset
              v-for="(product, index) in offerProductsDraft"
              :key="index"
              class="admin-product-editor-row"
            >
              <legend>Produit {{ index + 1 }}</legend>
              <label :for="`offer-product-name-${offer.offerId}-${index}`">Produit</label>
              <input
                :id="`offer-product-name-${offer.offerId}-${index}`"
                v-model="product.name"
                maxlength="128"
                required
              />
              <label :for="`offer-product-quantity-${offer.offerId}-${index}`">
                Quantité / présence
              </label>
              <input
                :id="`offer-product-quantity-${offer.offerId}-${index}`"
                v-model="product.quantity"
                type="number"
                min="0"
                max="999"
                required
              />
              <label :for="`offer-product-cost-${offer.offerId}-${index}`">
                Prix d’achat unitaire EUR
              </label>
              <input
                :id="`offer-product-cost-${offer.offerId}-${index}`"
                v-model="product.purchasePriceEuros"
                inputmode="decimal"
                required
              />
              <button
                class="button button-secondary"
                type="button"
                @click="removeOfferProduct(index)"
              >
                Retirer ce produit
              </button>
            </fieldset>
            <button class="button button-secondary" type="button" @click="addOfferProduct">
              Ajouter un produit
            </button>
            <button class="button button-primary" type="submit" :disabled="saving">
              Enregistrer la composition
            </button>
          </form>
          <div class="admin-actions">
            <button
              class="button button-primary"
              type="button"
              :disabled="saving"
              :data-testid="`duplicate-offer-${offer.offerId}`"
              @click="duplicateOffer(offer)"
            >
              Dupliquer
            </button>
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`edit-offer-coefficient-${offer.offerId}`"
              @click="beginOfferCoefficientEdit(offer)"
            >
              Modifier coef
            </button>
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`edit-offer-price-${offer.offerId}`"
              @click="beginOfferPriceEdit(offer)"
            >
              Modifier le prix
            </button>
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`edit-offer-products-${offer.offerId}`"
              @click="beginOfferProductsEdit(offer)"
            >
              Modifier les produits
            </button>
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`edit-offer-name-${offer.offerId}`"
              @click="beginOfferNameEdit(offer)"
            >
              Renommer
            </button>
          </div>
        </article>
      </div>
      <p
        v-else-if="!offersLoading && !offersError"
        class="flow-status"
        data-testid="catalog-filter-empty"
      >
        Aucun résultat.
      </p>
    </section>

    <section v-if="showPhysicalBoxes" class="admin-boxes-section" aria-labelledby="boxes-title">
      <div class="admin-section-header">
        <div>
          <p class="admin-section-kicker">Parc physique actuel</p>
          <h2 id="boxes-title">Boxes physiques</h2>
        </div>
        <span v-if="boxes.length" class="admin-box-count">{{ filteredBoxes.length }}</span>
      </div>
      <details class="admin-create-panel">
        <summary>Créer manuellement une Box physique</summary>
        <form
          class="admin-create-form"
          data-testid="create-box-form"
          @submit.prevent="createBox"
        >
          <p class="admin-box-prefix" aria-label="Préfixe automatique">Box_</p>
          <label for="new-box-number">Numéro</label>
          <input
            id="new-box-number"
            v-model="newBoxNumber"
            inputmode="numeric"
            pattern="[1-9][0-9]*"
            required
          />
          <label for="new-box-owner">Propriétaire</label>
          <select
            id="new-box-owner"
            v-model="newBoxOwner"
            required
            @change="resetNewBoxApartments"
          >
            <option value="" disabled>Sélectionner un propriétaire</option>
            <option v-for="owner in boxOwnerOptions" :key="owner" :value="owner">
              {{ owner }}
            </option>
          </select>
          <fieldset class="admin-box-apartment-selection">
            <legend>Appartements</legend>
            <p v-if="!newBoxOwner" class="flow-status">Sélectionnez d’abord un propriétaire.</p>
            <p v-else-if="boxApartmentOptions.length === 0" class="flow-error" role="alert">
              Aucun appartement configuré pour ce propriétaire.
            </p>
            <label
              v-for="configuration in boxApartmentOptions"
              :key="configuration.apartment"
              class="admin-apartment-card"
              :class="{
                'is-selected': newBoxApartments.includes(configuration.apartment),
              }"
              :data-testid="`admin-apartment-card-${configuration.apartment}`"
            >
              <input
                v-model="newBoxApartments"
                type="checkbox"
                :value="configuration.apartment"
              />
              <img
                v-if="configuration.listingPhotoDataUrl"
                :src="configuration.listingPhotoDataUrl"
                :alt="`Photo d’annonce de l’appartement ${configuration.apartment}`"
              />
              <span v-else class="admin-apartment-card-photo-missing" aria-hidden="true">
                Photo non renseignée
              </span>
              <span class="admin-apartment-card-copy">
                <strong>{{ configuration.apartment }}</strong>
                <small>N° de rue · {{ configuration.streetNumber ?? 'inconnu' }}</small>
                <em>{{
                  newBoxApartments.includes(configuration.apartment)
                    ? 'Sélectionné'
                    : 'Sélectionner'
                }}</em>
              </span>
            </label>
          </fieldset>
          <p v-if="mixedBoxStreetNumbers" class="flow-error" role="alert">
            Les appartements sélectionnés doivent avoir le même numéro de rue.
          </p>
          <label for="new-box-code">Code initial</label>
          <input
            id="new-box-code"
            v-model="newLockCode"
            inputmode="numeric"
            pattern="[0-9]{3}"
            maxlength="3"
            required
            @focus="clearDefaultNewLockCode"
          />
          <button class="button button-primary" type="submit" :disabled="saving">
            Créer la Box physique
          </button>
        </form>
      </details>
      <p v-if="boxesLoading" class="flow-status" role="status">Chargement des Boxes…</p>
      <p v-else-if="boxesError" class="flow-error" role="alert">{{ boxesError }}</p>
      <div
        v-if="filteredBoxes.length"
        class="admin-box-list"
        aria-label="Liste des boxes physiques"
      >
        <article
          v-for="box in filteredBoxes"
          :key="box.boxId"
          class="admin-box-card"
          :class="{
            'admin-box-card-damaged': ['THEFT', 'BREAKAGE'].includes(
              box.conditionState ?? 'ACTIVE',
            ),
          }"
        >
          <div>
            <h3 class="admin-box-business-name">{{ adminBoxLabel(box) }}</h3>
            <small
              v-if="!isCanonicalAdminBoxLabel(box.displayName)"
              class="admin-box-name-warning"
            >
              Libellé métier à renseigner au format
              Box_&lt;N&gt;_&lt;PROPRIÉTAIRE&gt;_&lt;ADRESSE&gt;.
            </small>
            <p
              class="admin-availability"
              :class="`admin-availability-${box.availabilityState.toLowerCase()}`"
              :data-testid="`box-availability-${box.boxId}`"
            >
              {{ boxAvailabilityLabel(box) }}
            </p>
            <p class="admin-lock-code" :data-testid="`box-lock-code-${box.boxId}`">
              Code · <strong>{{ boxLockCodes[box.boxId] ?? 'Code non enregistré' }}</strong>
            </p>
            <p
              v-if="typeof boxLockCodeEntries[box.boxId]?.renewalDays === 'number'"
              class="admin-lock-renewal"
              :class="boxLockCodeEntries[box.boxId]?.renewalDue ? 'is-overdue' : 'is-current'"
              :data-testid="`box-lock-renewal-${box.boxId}`"
            >
              <template v-if="boxLockCodeEntries[box.boxId]?.renewalDue">
                Renouvellement dépassé de
                {{ Math.abs(boxLockCodeEntries[box.boxId]?.renewalDays ?? 0) }} jour(s)
              </template>
              <template v-else>
                {{ boxLockCodeEntries[box.boxId]?.renewalDays }} jour(s) avant renouvellement
              </template>
            </p>
          </div>
          <form v-if="editingNameBoxId === box.boxId" @submit.prevent="saveBox(box)">
            <label :for="`name-${box.boxId}`">Nom métier</label>
            <input :id="`name-${box.boxId}`" v-model="displayName" maxlength="128" required />
            <button class="button button-primary" type="submit" :disabled="saving">
              Renommer
            </button>
          </form>
          <form
            v-if="editingLockCodeBoxId === box.boxId"
            :data-testid="`lock-code-form-${box.boxId}`"
            @submit.prevent="saveLockCode(box)"
          >
            <label :for="`lock-code-${box.boxId}`">Nouveau code à 3 chiffres</label>
            <input
              :id="`lock-code-${box.boxId}`"
              v-model="lockCodeDraft"
              inputmode="numeric"
              pattern="[0-9]{3}"
              maxlength="3"
              required
            />
            <button class="button button-primary" type="submit" :disabled="saving">
              Enregistrer le code
            </button>
          </form>
          <BoxWithdrawal
            :box-id="box.boxId"
            :request-id="box.withdrawalRequestId"
            role="admin"
            :disabled="saving"
            @changed="loadAdminData"
          />
          <div class="admin-actions">
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`edit-name-${box.boxId}`"
              @click="beginNameEdit(box)"
            >
              Renommer
            </button>
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`change-lock-code-${box.boxId}`"
              :disabled="(box.conditionState ?? 'ACTIVE') !== 'ACTIVE'"
              @click="beginLockCodeEdit(box)"
            >
              Changer code
            </button>
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`show-qr-${box.boxId}`"
              @click="showQr(box)"
            >
              QR
            </button>
            <button
              class="button button-secondary"
              type="button"
              :data-testid="`delete-${box.boxId}`"
              @click="deleteBox(box)"
            >
              Supprimer
            </button>
          </div>
        </article>
      </div>
      <p
        v-else-if="!boxesLoading && !boxesError"
        class="flow-status"
        data-testid="boxes-filter-empty"
      >
        Aucun résultat.
      </p>
    </section>

    <section
      v-if="selected && showPhysicalBoxes"
      class="admin-qr-sheet"
      aria-labelledby="qr-print-title"
    >
      <h2 id="qr-print-title" class="admin-box-business-name">
        {{ adminBoxLabel(selected) }}
      </h2>
      <div class="admin-qr-plate" data-testid="admin-qr-white-plate">
        <img :src="selectedQr" :alt="`QR voyageur de ${adminBoxLabel(selected)}`" />
      </div>
      <p>Scannez pour accéder à votre box.</p>
      <button class="button button-primary print-control" type="button" @click="printQr">
        Imprimer / réimprimer
      </button>
    </section>
    <OperationalNavigation
      :active="
        props.managementMode || showPhysicalBoxes ? 'more' : showCatalog ? 'catalog' : activeTab
      "
      :items="navigation"
      @select="selectTab"
    />
  </main>
</template>

<style scoped>
.management-menu {
  display: grid;
  gap: 0.8rem;
  margin: 1rem 0;
}
.management-menu > button,
.management-menu > a {
  display: grid;
  grid-template-columns: 2.6rem minmax(0, 1fr) 1rem;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1.1rem;
  border: 1px solid #d7dde6;
  border-radius: 1rem;
  background: white;
  color: #0c2744;
  text-align: left;
  text-decoration: none;
  font: inherit;
  cursor: pointer;
  box-sizing: border-box;
}
.management-menu strong,
.management-menu small {
  display: block;
}
.management-menu small {
  margin-top: 0.3rem;
  font-size: 0.85rem;
  color: #4b5563;
}
.management-icon {
  display: grid;
  place-items: center;
  background: #edf0f6;
  border-radius: 0.7rem;
  width: 2.6rem;
  height: 2.6rem;
  font-size: 1.5rem;
}
.management-menu > :focus-visible {
  outline: 3px solid #202d85;
  outline-offset: 3px;
}
.management-heading {
  margin-block: 1rem;
}
</style>
